import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Save, Loader2, Calendar, MapPin, Image as ImageIcon,
  Clock, BarChart3, ShoppingCart, Music, Ticket, UserPlus, Users,
  QrCode, Settings, Eye, EyeOff, Trash2, Upload, Plus, Edit2, AlertTriangle,
  Lock, XCircle, CheckCircle, Send, Edit, Info, Globe, Copy, Link, ExternalLink,
  Download, Check, X, Tag, Mail, FileText, AlertCircle, MessageSquare, Sparkles, Activity, Megaphone, Gift, Star
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import ProducerLayout from '../../components/producer/ProducerLayout';
import CloudinaryUploader from '../../components/CloudinaryUploader';
import VenueStatsTab from '../../components/producer/VenueStatsTab';
import ProducerInquiriesTab from '../../components/producer/ProducerInquiriesTab';
import CoProductionTab from '../../components/producer/CoProductionTab';
import HeatmapTab from '../../components/producer/HeatmapTab';
import OpenCallsProducerTab from '../../components/producer/OpenCallsProducerTab';
import { useProducerAuth } from '../../context/ProducerAuthContext';
import { isoToLocalDateTimeInput, localDateTimeInputToISO } from '../../lib/datetime';

const TABS = [
  { id: 'info', label: 'Información', icon: Settings, permissions: ['manage_events'] },
  { id: 'tickets', label: 'Entradas', icon: Ticket, permissions: ['manage_tickets'] },
  { id: 'inquiries', label: 'Consultas', icon: MessageSquare, permissions: ['manage_tickets'] },
  { id: 'venue', label: 'Venue', icon: MapPin, permissions: ['manage_events'], requiresVenue: true },
  { id: 'reports', label: 'Reportes', icon: BarChart3, permissions: ['view_reports'] },
  { id: 'orders', label: 'Órdenes', icon: ShoppingCart, permissions: ['view_reports'] },
  { id: 'lineup', label: 'Lineup', icon: Music, permissions: ['manage_events'] },
  { id: 'opencalls', label: 'Convocatorias', icon: Megaphone, permissions: ['manage_events'] },
  { id: 'invitations', label: 'Invitaciones', icon: UserPlus, permissions: ['manage_invitations'] },
  { id: 'rrpp', label: 'RRPP', icon: Users, permissions: ['manage_rrpp'] },
  { id: 'heatmap', label: 'Heatmap', icon: Activity, permissions: ['view_reports', 'manage_events'] },
  { id: 'co_production', label: 'Co-producción', icon: Users, permissions: ['manage_events'] },
  { id: 'scanner', label: 'Escanear', icon: QrCode, permissions: ['scan_tickets'] },
];

const STATUS_CONFIG = {
  draft: { 
    label: 'Borrador', 
    color: 'bg-zinc-500', 
    canEditInfo: true,  // Can edit general info
    canEditTickets: true,
    canEditLineup: true,
    canEditInvitations: true,
    canEditRRPP: true
  },
  pending: { 
    label: 'Pendiente de Aprobación', 
    color: 'bg-yellow-500', 
    canEditInfo: false,  // Cannot edit until reviewed
    canEditTickets: false,
    canEditLineup: false,
    canEditInvitations: false,
    canEditRRPP: false
  },
  pending_approval: { 
    label: 'Pendiente de Aprobación', 
    color: 'bg-yellow-500', 
    canEditInfo: false,
    canEditTickets: false,
    canEditLineup: false,
    canEditInvitations: false,
    canEditRRPP: false
  },
  published: { 
    label: 'Publicado', 
    color: 'bg-green-500', 
    canEditInfo: false,   // Title/description locked
    canEditLocation: true, // Can edit venue, address, google maps
    canEditDate: true,     // Can edit date/time
    canEditTickets: true,  // Can manage tickets
    canEditLineup: true,   // Can manage lineup
    canEditInvitations: true,  // Can send invitations
    canEditRRPP: true      // Can manage RRPP
  },
  completed: { 
    label: 'Realizado', 
    color: 'bg-blue-500', 
    canEditInfo: true,     // Allow editing info after completion
    canEditLocation: true, // Can edit venue, address
    canEditDate: true,     // Can edit date/time
    canEditTickets: true,  // Can manage tickets
    canEditLineup: true,   // Can manage lineup
    canEditInvitations: true,
    canEditRRPP: true,
    canChangeStatus: true  // Can change status back to published if needed
  },
  cancelled: { 
    label: 'Cancelado', 
    color: 'bg-red-500', 
    canEditInfo: false,
    canEditTickets: false,
    canEditLineup: false,
    canEditInvitations: false,
    canEditRRPP: false
  },
  locked: { 
    label: 'Bloqueado', 
    color: 'bg-red-700', 
    canEditInfo: false,
    canEditTickets: false,
    canEditLineup: false,
    canEditInvitations: false,
    canEditRRPP: false
  },
};

const EventManagePage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission, isSecondaryAdmin, isPremium } = useProducerAuth();
  const [event, setEvent] = useState(null);
  const [producer, setProducer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [uploading, setUploading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [showSendForApprovalModal, setShowSendForApprovalModal] = useState(false);

  // Helper to check if user has permission for a tab
  const hasTabPermission = (tab) => {
    if (!tab.permissions || tab.permissions.length === 0) return true;
    return hasAnyPermission(tab.permissions);
  };

  // Find first accessible tab for user
  const getFirstAccessibleTab = () => {
    const accessible = TABS.find(tab => hasTabPermission(tab));
    return accessible ? accessible.id : 'info';
  };

  useEffect(() => {
    // If current tab is not accessible, switch to first accessible
    if (!hasTabPermission(TABS.find(t => t.id === activeTab))) {
      setActiveTab(getFirstAccessibleTab());
    }
  }, [activeTab]);

  useEffect(() => {
    loadEvent();
    loadProducer();
  }, [eventId]);

  const loadProducer = async () => {
    try {
      const response = await api.get('/producers/me');
      setProducer(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const loadEvent = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar evento');
      navigate('/producer/events');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const statusConfig = STATUS_CONFIG[event.status] || {};
    
    // Check if event is locked or cancelled completely
    if (event.is_locked || event.status === 'cancelled') {
      toast.error('Este evento está bloqueado y no puede ser modificado');
      return;
    }
    
    // Check permissions - for published events, only location and date can be edited
    const canEditLocation = statusConfig.canEditLocation || statusConfig.canEditInfo;
    const canEditDate = statusConfig.canEditDate || statusConfig.canEditInfo;
    
    if (!statusConfig.canEditInfo && !canEditLocation && !canEditDate) {
      toast.error('Este evento no puede ser modificado.');
      return;
    }

    setSaving(true);
    try {
      // Build update payload based on what can be edited
      const updatePayload = {};
      
      // Info fields (title, description) - only if canEditInfo
      if (statusConfig.canEditInfo) {
        updatePayload.title = event.title;
        updatePayload.description = event.description;
        updatePayload.image_url = event.image_url;
      }
      
      // Location fields - if canEditLocation or canEditInfo
      if (canEditLocation) {
        updatePayload.location = event.location;
        updatePayload.address = event.address;
        updatePayload.venue_name = event.venue_name;
        updatePayload.venue_map_image_url = event.venue_map_image_url || '';
        updatePayload.google_maps_url = event.google_maps_url || event.address;
      }
      
      // Date fields - if canEditDate or canEditInfo
      if (canEditDate) {
        updatePayload.date = event.date;
        updatePayload.end_date = event.end_date;
      }
      
      // Status - only allow change for verified producers
      if (producer?.is_verified) {
        updatePayload.status = event.status;
      }
      
      // Order/ticket limits — always send (null = no limit, > 0 = enforced)
      updatePayload.max_orders_per_user = event.max_orders_per_user || null;
      
      // Custom terms — only send if effectively unlocked (draft events stay editable)
      const _termsEffectivelyLocked = event.custom_terms_locked && event.status !== 'draft';
      if (event.custom_terms !== undefined && !_termsEffectivelyLocked) {
        updatePayload.custom_terms = event.custom_terms?.trim() || null;
      }
      // Always allow toggling the "include in email" preference
      updatePayload.include_terms_in_email = !!event.include_terms_in_email;
      // Public-comment moderation toggle
      updatePayload.require_comment_approval = !!event.require_comment_approval;
      // Spam filter (default ON when undefined)
      updatePayload.spam_filter_enabled = event.spam_filter_enabled !== false;
      
      await api.put(`/producers/me/events/${eventId}`, updatePayload);
      toast.success('Evento guardado');
      loadEvent();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSendForApproval = async () => {
    setSaving(true);
    try {
      await api.post(`/producers/me/events/${eventId}/send-for-approval`);
      toast.success('Evento enviado para aprobación');
      setShowSendForApprovalModal(false);
      loadEvent();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      await api.put(`/producers/me/events/${eventId}`, {
        ...event,
        status: 'published'
      });
      toast.success('¡Evento publicado exitosamente!');
      loadEvent();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al publicar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStatus = async (newStatus) => {
    setSaving(true);
    try {
      await api.put(`/producers/me/events/${eventId}`, {
        status: newStatus
      });
      toast.success(`Estado del evento actualizado a ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      loadEvent();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEvent = async () => {
    if (cancelConfirmText !== 'CANCELAR EVENTO') {
      toast.error('Escribe "CANCELAR EVENTO" para confirmar');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/producers/me/events/${eventId}/cancel`);
      toast.success('Evento cancelado');
      setShowCancelModal(false);
      loadEvent();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (event.is_locked || event.status === 'cancelled') {
      toast.error('Este evento está bloqueado');
      return;
    }

    setUploading(true);
    try {
      const sigResponse = await api.get('/cloudinary/signature?folder=producer_events');
      const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      const uploadData = await uploadResponse.json();
      
      if (uploadData.secure_url) {
        setEvent(prev => ({ ...prev, image_url: uploadData.secure_url }));
        toast.success('Imagen subida');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  // Check if event is editable
  // Check permissions based on event status
  const getEditPermissions = () => {
    const statusConfig = STATUS_CONFIG[event?.status] || {};
    const isLocked = event?.is_locked || event?.status === 'cancelled';
    
    return {
      canEditInfo: !isLocked && statusConfig.canEditInfo,
      canEditLocation: !isLocked && (statusConfig.canEditLocation || statusConfig.canEditInfo),
      canEditDate: !isLocked && (statusConfig.canEditDate || statusConfig.canEditInfo),
      canEditTickets: !isLocked && statusConfig.canEditTickets,
      canEditLineup: !isLocked && statusConfig.canEditLineup,
      canEditInvitations: !isLocked && statusConfig.canEditInvitations,
      canEditRRPP: !isLocked && statusConfig.canEditRRPP,
    };
  };
  
  const permissions = getEditPermissions();
  
  // Legacy function for backward compatibility
  const isEditable = () => {
    return permissions.canEditInfo;
  };

  if (loading) {
    return (
      <ProducerLayout currentPage="events">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-violet-500" size={32} />
        </div>
      </ProducerLayout>
    );
  }

  // Show locked message
  if (event?.is_locked || event?.status === 'cancelled') {
    return (
      <ProducerLayout currentPage="events">
        <div className="p-4 lg:p-8 max-w-4xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 text-center">
            <Lock className="mx-auto mb-4 text-red-500" size={48} />
            <h2 className="text-2xl font-bold text-white mb-2">
              {event?.status === 'cancelled' ? 'Evento Cancelado' : 'Evento Bloqueado'}
            </h2>
            <p className="text-zinc-400 mb-4">
              {event?.status === 'cancelled' 
                ? 'Este evento fue cancelado y no puede ser modificado.'
                : 'Este evento está bloqueado. No se pueden realizar modificaciones.'}
            </p>
            <p className="text-sm text-zinc-500 mb-6">
              Para desbloquear este evento, contacta a soporte:<br/>
              <a href="mailto:central@bravoproductora.com" className="text-violet-500 hover:underline">
                central@bravoproductora.com
              </a>
            </p>
            <Button
              onClick={() => navigate('/producer/events')}
              variant="outline"
              className="border-zinc-700"
            >
              <ArrowLeft size={16} className="mr-2" />
              Volver a Mis Eventos
            </Button>
          </div>
        </div>
      </ProducerLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[event?.status] || STATUS_CONFIG.draft;

  return (
    <ProducerLayout currentPage="events">
      <div className="p-4 lg:p-6 min-h-screen overflow-x-hidden" data-testid="event-manage-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button 
              onClick={() => navigate('/producer/events')}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0"
            >
              <ArrowLeft size={20} className="text-zinc-400" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl lg:text-2xl font-black text-white truncate">{event?.title}</h1>
              <p className="text-zinc-500 text-sm">
                {event?.date && new Date(event.date).toLocaleDateString('es-AR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'America/Argentina/Buenos_Aires'
                })}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 lg:gap-3">
            {/* Status Badge */}
            <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
            
            <Button
              variant="outline"
              onClick={() => window.open(`/evento/${eventId}`, '_blank')}
              className="border-zinc-700"
            >
              <Eye size={16} className="mr-2" />
              Ver página
            </Button>

            {/* Crear campaña — for any plan, gated by quota on the backend */}
            {(event?.status === 'published' || event?.status === 'completed' || event?.status === 'ended') && (
              <Button
                onClick={() => navigate(`/producer/email-metrics?compose=1&event=${eventId}`)}
                className="bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] hover:from-[#F5D25A] hover:to-[#D4AF37] text-black font-bold"
                data-testid="create-campaign-btn"
                title="Crear una campaña de email para los compradores de este evento"
              >
                <Megaphone size={16} className="mr-2" />
                Crear campaña
              </Button>
            )}
            
            {/* Save button - always show for draft events */}
            {event?.status === 'draft' && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Borrador
              </Button>
            )}
            
            {/* Save button for published events - location/date edits */}
            {event?.status === 'published' && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Cambios
              </Button>
            )}
            
            {/* Save button for completed events - producers can still edit info */}
            {event?.status === 'completed' && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar Cambios
              </Button>
            )}
            
            {/* Publish button - only for verified producers with draft events */}
            {producer?.is_verified && event?.status === 'draft' && (
              <Button
                onClick={handlePublish}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Globe size={16} className="mr-2" />}
                Publicar
              </Button>
            )}
          </div>
        </div>

        {/* Non-verified producer warning - show send for approval button */}
        {!producer?.is_verified && event?.status === 'draft' && !event?.rejection_reason && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-yellow-500" size={20} />
              <div>
                <p className="text-yellow-400 font-medium">Evento en borrador</p>
                <p className="text-yellow-400/70 text-sm">
                  Tu productora no está verificada. Debes enviar el evento para aprobación antes de publicarlo.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowSendForApprovalModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <Send size={16} className="mr-2" />
              Enviar para Aprobación
            </Button>
          </div>
        )}

        {/* Rejection notice - show when event was rejected */}
        {event?.rejection_reason && event?.status === 'draft' && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <XCircle className="text-red-400 mt-0.5 flex-shrink-0" size={22} />
              <div className="flex-1">
                <p className="text-red-400 font-bold">Evento Rechazado</p>
                <p className="text-red-300/80 text-sm mt-2 leading-relaxed">{event.rejection_reason}</p>
                {event.rejected_at && (
                  <p className="text-red-400/50 text-xs mt-3">
                    Rechazado el {new Date(event.rejected_at).toLocaleDateString('es-AR', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
                <div className="mt-4 pt-4 border-t border-red-500/20">
                  <p className="text-zinc-400 text-sm mb-3">Corrige los problemas señalados y vuelve a enviar para aprobación:</p>
                  <Button
                    onClick={() => setShowSendForApprovalModal(true)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Send size={16} className="mr-2" />
                    Reenviar para Aprobación
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Verified producer info - draft events */}
        {producer?.is_verified && event?.status === 'draft' && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <CheckCircle className="text-green-500" size={20} />
            <div>
              <p className="text-green-400 font-medium">Productora verificada</p>
              <p className="text-green-400/70 text-sm">
                Puedes publicar este evento directamente cuando esté listo.
              </p>
            </div>
          </div>
        )}

        {/* Pending approval notice */}
        {event?.status === 'pending_approval' && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Clock className="text-yellow-500" size={20} />
            <div>
              <p className="text-yellow-400 font-medium">Pendiente de aprobación</p>
              <p className="text-yellow-400/70 text-sm">
                El evento está siendo revisado por nuestro equipo. No puedes realizar modificaciones hasta que sea aprobado.
              </p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-6 border-b border-zinc-800 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-zinc-700">
          {TABS.filter(t => !t.requiresVenue || !!event?.venue_id).map((tab) => {
            const Icon = tab.icon;
            const tabAllowed = hasTabPermission(tab);
            
            if (!tabAllowed) {
              return (
                <div
                  key={tab.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-t-lg whitespace-nowrap text-sm cursor-not-allowed opacity-40 grayscale"
                  title="No tenés permiso para acceder a esta sección"
                >
                  <div className="relative">
                    <Icon size={14} className="text-zinc-500" />
                    <Lock size={8} className="absolute -bottom-0.5 -right-0.5 text-zinc-600" />
                  </div>
                  <span className="hidden sm:inline text-zinc-500">{tab.label}</span>
                </div>
              );
            }
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-t-lg whitespace-nowrap transition-colors text-sm ${
                  activeTab === tab.id
                    ? 'bg-zinc-800 text-white border-b-2 border-violet-500'
                    : 'text-zinc-500 hover:text-white hover:bg-zinc-800/50'
                }`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {activeTab === 'info' && (
              <InfoTab 
                event={event} 
                setEvent={setEvent} 
                uploading={uploading}
                handleImageUpload={handleImageUpload}
                producer={producer}
                isEditable={permissions.canEditInfo}
                canEditLocation={permissions.canEditLocation}
                canEditDate={permissions.canEditDate}
                onCancelClick={() => setShowCancelModal(true)}
              />
            )}
            {activeTab === 'tickets' && <TicketsTab eventId={eventId} isEditable={permissions.canEditTickets} />}
            {activeTab === 'inquiries' && <ProducerInquiriesTab eventId={eventId} />}
            {activeTab === 'venue' && <VenueStatsTab eventId={eventId} event={event} />}
            {activeTab === 'reports' && <ReportsTab eventId={eventId} />}
            {activeTab === 'orders' && <OrdersTab eventId={eventId} />}
            {activeTab === 'lineup' && <LineupTab eventId={eventId} isEditable={permissions.canEditLineup} />}
            {activeTab === 'opencalls' && <OpenCallsProducerTab eventId={eventId} isEditable={permissions.canEditLineup} />}
            {activeTab === 'invitations' && <InvitationsTab eventId={eventId} isEditable={permissions.canEditInvitations} />}
            {activeTab === 'rrpp' && <RRPPTab eventId={eventId} isEditable={permissions.canEditRRPP} />}
            {activeTab === 'heatmap' && <HeatmapTab eventId={eventId} />}
            {activeTab === 'co_production' && <CoProductionTab eventId={eventId} />}
            {activeTab === 'scanner' && <ScannerTab eventId={eventId} />}
          </motion.div>
        </AnimatePresence>

        {/* Cancel Event Modal */}
        <AnimatePresence>
          {showCancelModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-zinc-900 border border-red-500/30 rounded-xl max-w-md w-full p-6"
              >
                <div className="text-center mb-6">
                  <AlertTriangle className="mx-auto mb-4 text-red-500" size={48} />
                  <h3 className="text-xl font-bold text-white mb-2">¿Cancelar Evento?</h3>
                  <p className="text-zinc-400 text-sm">
                    Esta acción <strong className="text-red-400">NO tiene vuelta atrás</strong>. 
                    El evento quedará bloqueado y no podrás modificarlo. 
                    Solo podrá desbloquearse contactando a soporte.
                  </p>
                </div>

                <div className="mb-4">
                  <Label className="text-zinc-400 text-sm">
                    Escribe <strong className="text-red-400">CANCELAR EVENTO</strong> para confirmar
                  </Label>
                  <Input
                    value={cancelConfirmText}
                    onChange={(e) => setCancelConfirmText(e.target.value)}
                    placeholder="CANCELAR EVENTO"
                    className="bg-zinc-800 border-zinc-700 mt-2 text-center font-mono"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCancelModal(false);
                      setCancelConfirmText('');
                    }}
                    className="flex-1 border-zinc-700"
                  >
                    No, volver
                  </Button>
                  <Button
                    onClick={handleCancelEvent}
                    disabled={saving || cancelConfirmText !== 'CANCELAR EVENTO'}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <XCircle size={16} className="mr-2" />}
                    Cancelar Evento
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Send for Approval Modal */}
        <AnimatePresence>
          {showSendForApprovalModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6"
              >
                <div className="text-center mb-6">
                  <Send className="mx-auto mb-4 text-violet-500" size={48} />
                  <h3 className="text-xl font-bold text-white mb-2">Enviar para Aprobación</h3>
                  <p className="text-zinc-400 text-sm">
                    Una vez enviado, no podrás modificar el evento hasta que sea aprobado o rechazado por nuestro equipo.
                  </p>
                </div>

                <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                  <h4 className="font-bold text-white mb-2">Checklist antes de enviar:</h4>
                  <ul className="text-sm text-zinc-400 space-y-1">
                    <li>✓ Información del evento completa</li>
                    <li>✓ Flyer del evento subido</li>
                    <li>✓ Tandas de entradas configuradas</li>
                    <li>✓ Fecha y ubicación correctas</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSendForApprovalModal(false)}
                    className="flex-1 border-zinc-700"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSendForApproval}
                    disabled={saving}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                  >
                    {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                    Enviar
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </ProducerLayout>
  );
};

// Info Tab Component
const InfoTab = ({ event, setEvent, uploading, handleImageUpload, producer, isEditable, canEditLocation, canEditDate, onCancelClick }) => {
  const [sendingPreview, setSendingPreview] = useState(false);
  const [mapUploading, setMapUploading] = useState(false);

  const handleMapImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMapUploading(true);
    try {
      const sig = await api.get('/cloudinary/signature?folder=venue_maps');
      const { signature, timestamp, cloud_name, api_key, folder } = sig.data;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', api_key);
      fd.append('timestamp', timestamp);
      fd.append('signature', signature);
      fd.append('folder', folder);
      const up = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: 'POST', body: fd });
      const data = await up.json();
      if (!data.secure_url) throw new Error('upload failed');
      setEvent(prev => ({ ...prev, venue_map_image_url: data.secure_url }));
      toast.success('Mapa cargado — guardá los cambios para publicarlo');
    } catch {
      toast.error('Error al subir el mapa');
    } finally {
      setMapUploading(false);
    }
  };

  const handleSendEmailPreview = async () => {
    if (!event?.id) return;
    setSendingPreview(true);
    try {
      const res = await api.post(`/producers/me/events/${event.id}/email-preview`, { force_terms: true });
      toast.success(res.data?.message || 'Vista previa enviada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo enviar la vista previa');
    } finally {
      setSendingPreview(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Info Lock Banner */}
      {!isEditable && event?.status === 'published' && (
        <div className="xl:col-span-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
          <Lock className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-amber-400 font-medium">Título y descripción bloqueados</p>
            <p className="text-amber-400/70 text-sm mt-1">
              El evento ya está publicado. Para modificar el nombre y descripción, 
              contacta a <a href="mailto:central@bravoproductora.com" className="underline">central@bravoproductora.com</a>
            </p>
            <p className="text-green-400/70 text-sm mt-2">
              Puedes editar la ubicación, fecha/hora, entradas, invitaciones, RRPP y lineup.
            </p>
          </div>
        </div>
      )}
      
      {/* Main Info */}
      <div className="xl:col-span-2 space-y-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-4">Información General</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Nombre del evento</Label>
              <Input
                value={event?.title || ''}
                onChange={(e) => setEvent(prev => ({ ...prev, title: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!isEditable}
              />
            </div>
            <div>
              <Label className="text-zinc-400">Descripción</Label>
              <Textarea
                value={event?.description || ''}
                onChange={(e) => setEvent(prev => ({ ...prev, description: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                rows={4}
                disabled={!isEditable}
              />
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-4">Ubicación</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Dirección</Label>
              <Input
                value={event?.location || ''}
                onChange={(e) => setEvent(prev => ({ ...prev, location: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!canEditLocation}
              />
            </div>
            <div>
              <Label className="text-zinc-400">Nombre del venue (opcional)</Label>
              <Input
                value={event?.venue_name || ''}
                onChange={(e) => setEvent(prev => ({ ...prev, venue_name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!canEditLocation}
              />
            </div>
            <div>
              <Label className="text-zinc-400">Link Google Maps</Label>
              <Input
                value={event?.google_maps_url || event?.address || ''}
                onChange={(e) => setEvent(prev => ({ ...prev, google_maps_url: e.target.value, address: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!canEditLocation}
                placeholder="https://maps.google.com/..."
              />
            </div>
            <div>
              <Label className="text-zinc-400">Mapa del venue (imagen)</Label>
              {event?.venue_map_image_url ? (
                <div className="mt-2 relative inline-block">
                  <img src={event.venue_map_image_url} alt="Mapa del venue" className="max-h-44 rounded-xl border border-zinc-700" data-testid="venue-map-image-preview" />
                  {canEditLocation && (
                    <button
                      type="button"
                      onClick={() => setEvent(prev => ({ ...prev, venue_map_image_url: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-400 text-white rounded-full p-1.5"
                      title="Quitar mapa"
                      data-testid="venue-map-image-remove"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ) : (
                <label className={`mt-2 flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-zinc-700 text-zinc-400 text-sm ${canEditLocation ? 'cursor-pointer hover:border-zinc-500' : 'opacity-50'}`}>
                  {mapUploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  {mapUploading ? 'Subiendo...' : 'Subir imagen del mapa (plano del venue)'}
                  <input type="file" className="hidden" accept="image/*" onChange={handleMapImageUpload} disabled={!canEditLocation || mapUploading} data-testid="venue-map-image-input" />
                </label>
              )}
              <p className="text-[11px] text-zinc-600 mt-1">Se muestra en la página del evento en lugar del mapa interactivo. Guardá los cambios para publicarlo.</p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-4">Fecha y Hora</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Inicio</Label>
              <Input
                type="datetime-local"
                value={isoToLocalDateTimeInput(event?.date)}
                min={`${new Date().getFullYear() - 1}-01-01T00:00`}
                max={`${new Date().getFullYear() + 3}-12-31T23:59`}
                onChange={(e) => setEvent(prev => ({ ...prev, date: localDateTimeInputToISO(e.target.value) }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!canEditDate}
              />
              {event?.date && (
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  {new Date(event.date).toLocaleString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' })} hs
                </p>
              )}
            </div>
            <div>
              <Label className="text-zinc-400">Fin (opcional)</Label>
              <Input
                type="datetime-local"
                value={isoToLocalDateTimeInput(event?.end_date)}
                min={isoToLocalDateTimeInput(event?.date) || `${new Date().getFullYear() - 1}-01-01T00:00`}
                max={`${new Date().getFullYear() + 3}-12-31T23:59`}
                onChange={(e) => setEvent(prev => ({ ...prev, end_date: localDateTimeInputToISO(e.target.value) }))}
                className="bg-zinc-800 border-zinc-700 mt-1"
                disabled={!canEditDate}
              />
              {event?.end_date && (
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  {new Date(event.end_date).toLocaleString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' })} hs
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Custom Terms & Conditions */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-2 flex items-center gap-2">
            <FileText size={18} className="text-amber-500" />
            Términos y condiciones del evento
            {event?.custom_terms_locked && event?.status !== 'draft' && (
              <span className="text-[10px] uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                <Lock size={9} /> Bloqueado
              </span>
            )}
            {event?.status === 'draft' && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
                Borrador · editable
              </span>
            )}
          </h3>
          {(event?.custom_terms_locked && event?.status !== 'draft') ? (
            <>
              <p className="text-xs text-zinc-500 mb-3">
                Estos términos ya fueron registrados para este evento. Por seguridad legal solo el equipo de Bravo puede modificarlos. Si necesitás cambios, contactanos.
              </p>
              <div
                data-testid="custom-terms-locked"
                className="bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-3 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto"
              >
                {event.custom_terms}
              </div>
              {/* Toggle: include T&C in confirmation email (still editable even when terms are locked) */}
              <label className={`mt-4 flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${event.include_terms_in_email ? 'border-violet-500/50 bg-violet-500/5' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}>
                <input
                  type="checkbox"
                  checked={!!event.include_terms_in_email}
                  onChange={(e) => setEvent(prev => ({ ...prev, include_terms_in_email: e.target.checked }))}
                  className="mt-0.5 accent-violet-500"
                  data-testid="include-terms-in-email"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">📧 Incluir los T&C en el email de confirmación</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Cada comprador recibirá los términos en el cuerpo del mail junto al QR. Útil como prueba de aceptación ante disputas con Mercado Pago.</p>
                </div>
              </label>
            </>
          ) : (
            <>
              <p className="text-xs text-amber-400/80 mb-3 flex items-start gap-1.5">
                <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                <span>{event?.status === 'draft'
                  ? <>Mientras el evento esté en <strong>borrador</strong> podés modificar libremente los términos. Cuando lo publiques, se <strong>bloquean</strong> y solo el equipo de Bravo podrá modificarlos.</>
                  : <>Una vez guardados, estos términos se <strong>bloquean</strong>. Solo el equipo de Bravo podrá modificarlos después.</>
                }</span>
              </p>
              <textarea
                value={event?.custom_terms || ''}
                onChange={(e) => setEvent(prev => ({ ...prev, custom_terms: e.target.value }))}
                rows={5}
                placeholder="Ej: El evento se realiza en una embarcación. Los asistentes declaran..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                disabled={!isEditable}
                data-testid="custom-terms-input"
              />
              <p className="text-xs text-zinc-500 mt-2">Opcional. Dejá vacío si no necesitás términos específicos.</p>
              <label className={`mt-4 flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${event.include_terms_in_email ? 'border-violet-500/50 bg-violet-500/5' : 'border-zinc-800 bg-zinc-900/40'} ${!event?.custom_terms?.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:border-zinc-700'}`}>
                <input
                  type="checkbox"
                  checked={!!event.include_terms_in_email}
                  onChange={(e) => setEvent(prev => ({ ...prev, include_terms_in_email: e.target.checked }))}
                  disabled={!event?.custom_terms?.trim()}
                  className="mt-0.5 accent-violet-500"
                  data-testid="include-terms-in-email"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">📧 Incluir los T&C en el email de confirmación</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Cada comprador recibirá los términos en el cuerpo del mail junto al QR. Útil como prueba de aceptación ante disputas con Mercado Pago.</p>
                </div>
              </label>
            </>
          )}

          {/* Email preview button — sends a sample confirmation to the producer's inbox */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm text-white font-medium flex items-center gap-2">
                  <Mail size={14} className="text-violet-400" />
                  Vista previa del email de confirmación
                </p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Te enviamos un mail de muestra a <span className="text-zinc-300">{producer?.email || 'tu casilla'}</span> con los T&C incluidos para que veas cómo lo recibe el comprador.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleSendEmailPreview}
                disabled={sendingPreview}
                className="bg-violet-500 hover:bg-violet-600 text-white whitespace-nowrap"
                data-testid="send-email-preview-btn"
              >
                {sendingPreview ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Enviando…</>
                ) : (
                  <><Mail size={14} className="mr-1.5" /> Enviar vista previa</>
                )}
              </Button>
            </div>
          </div>
        </div>


        {/* Public comments moderation */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-2 flex items-center gap-2">
            <MessageSquare size={18} className="text-violet-500" />
            Moderación de comentarios públicos
          </h3>
          <p className="text-xs text-zinc-500 mb-3">
            Activá esto si querés revisar los comentarios antes de que aparezcan en la página del evento. Útil en eventos masivos o sensibles para evitar spam y trolls.
          </p>
          <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${event.require_comment_approval ? 'border-violet-500/50 bg-violet-500/5' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}>
            <input
              type="checkbox"
              checked={!!event.require_comment_approval}
              onChange={(e) => setEvent(prev => ({ ...prev, require_comment_approval: e.target.checked }))}
              className="mt-0.5 accent-violet-500"
              data-testid="require-comment-approval-toggle"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-white">Aprobar comentarios antes de publicar</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Los comentarios nuevos quedarán en estado <span className="text-orange-300 font-semibold">"Por aprobar"</span> en tu Pipeline de Consultas. Aprobalos para hacerlos públicos o rechazalos.
              </p>
            </div>
          </label>

          <label className={`mt-3 flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${event.spam_filter_enabled !== false ? 'border-violet-500/50 bg-violet-500/5' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}>
            <input
              type="checkbox"
              checked={event.spam_filter_enabled !== false}
              onChange={(e) => setEvent(prev => ({ ...prev, spam_filter_enabled: e.target.checked }))}
              className="mt-0.5 accent-violet-500"
              data-testid="spam-filter-toggle"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-white flex items-center gap-1.5">
                🤖 Filtro automático de spam (IA)
              </p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Un clasificador en tiempo real detecta insultos, scams y promoción no relacionada. Los comentarios obviamente spam se rechazan solos; los dudosos van a tu cola "Por aprobar". Podés revertir cualquier falso positivo desde el filtro <span className="text-red-300 font-semibold">"Spam detectado"</span> del Pipeline.
              </p>
            </div>
          </label>
        </div>


        {/* Tickets per Purchase Limit */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Ticket size={18} className="text-violet-500" />
            Máximo de entradas por compra
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <Input
                type="number"
                value={event?.max_orders_per_user || ''}
                onChange={(e) => setEvent(prev => ({ 
                  ...prev, 
                  max_orders_per_user: e.target.value ? parseInt(e.target.value) : null 
                }))}
                placeholder="Sin límite"
                min={1}
                max={20}
                className="bg-zinc-800 border-zinc-700 w-32"
                disabled={!isEditable}
                data-testid="max-tickets-per-purchase"
              />
              <span className="text-zinc-400 text-sm">entrada(s) máximo por compra</span>
            </div>
            <p className="text-xs text-zinc-500">
              Limita cuántas entradas puede comprar una persona en <strong>una sola transacción</strong>. Vacío = sin límite.
            </p>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-bold text-white mb-4">Flyer</h3>
          <div className="aspect-[3/4] bg-zinc-800 rounded-lg overflow-hidden relative group">
            {event?.image_url ? (
              <img src={event.image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-600">
                <ImageIcon size={40} />
              </div>
            )}
            {isEditable && (
              <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                {uploading ? (
                  <Loader2 className="animate-spin text-white" size={32} />
                ) : (
                  <Upload className="text-white" size={32} />
                )}
                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        {/* Cancel Event Button - Available for draft, pending_approval, and published events */}
        {event?.status && !['cancelled', 'locked'].includes(event.status) && !event?.is_locked && (
          <Button
            onClick={onCancelClick}
            variant="outline"
            className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
          >
            <XCircle size={16} className="mr-2" />
            Cancelar Evento
          </Button>
        )}
      </div>
    </div>
  );
};

// Tickets Tab with full editing
const TicketsTab = ({ eventId, isEditable }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    name: '',
    price: 0,
    quantity: 100,
    max_per_order: 4,
    description: '',
    sale_end_date: '',
    entry_limit_time: '',
    is_visible: true,
    is_active: true,
    discount_code: '',
    discount_percent: 0,
    free_for_bravx: false,
    tier_type: 'sale',
    inquiry_link: '',
    inquiry_label: '',
    price_stages: [],
    promo_pay_x: 0,
    promo_get_y: 0,
    promo_valid_until: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTickets();
    // Auto-refresh tickets every 30 seconds
    const interval = setInterval(loadTickets, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTickets = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/tickets`);
      setTickets(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = () => {
    setEditingTicket(null);
    setTicketForm({
      name: '',
      price: 0,
      quantity: 100,
      max_per_order: 4,
      description: '',
      sale_end_date: '',
      entry_limit_time: '',
      is_visible: true,
      is_active: true,
      discount_code: '',
      discount_percent: 0,
      free_for_bravx: false,
      tier_type: 'sale',
      inquiry_link: '',
      inquiry_label: '',
      price_stages: [],
      promo_pay_x: 0,
      promo_get_y: 0,
      promo_valid_until: ''
    });
    setShowModal(true);
  };

  const openEditModal = (ticket) => {
    setEditingTicket(ticket);
    setTicketForm({
      name: ticket.name || '',
      price: ticket.price || 0,
      quantity: ticket.quantity || 100,
      max_per_order: ticket.max_per_order || 4,
      description: ticket.description || '',
      sale_end_date: ticket.sale_end_date || '',
      entry_limit_time: ticket.entry_limit_time || '',
      is_visible: ticket.is_visible !== false,
      is_active: ticket.is_active !== false,
      discount_code: ticket.discount_code || '',
      discount_percent: ticket.discount_percent || 0,
      free_for_bravx: ticket.free_for_bravx || false,
      tier_type: ticket.tier_type || 'sale',
      inquiry_link: ticket.inquiry_link || '',
      inquiry_label: ticket.inquiry_label || '',
      is_sold_out: !!ticket.is_sold_out,
      price_stages: Array.isArray(ticket.price_stages) ? ticket.price_stages : []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!ticketForm.name.trim()) {
      toast.error('Ingresa un nombre para la tanda');
      return;
    }
    
    setSaving(true);
    try {
      if (editingTicket) {
        await api.put(`/producers/me/events/${eventId}/tickets/${editingTicket.id}`, ticketForm);
        toast.success('Tanda actualizada');
      } else {
        await api.post(`/producers/me/events/${eventId}/tickets`, ticketForm);
        toast.success('Tanda creada');
      }
      setShowModal(false);
      loadTickets();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ticketId) => {
    if (!confirm('¿Eliminar esta tanda de entradas?')) return;
    
    try {
      await api.delete(`/producers/me/events/${eventId}/tickets/${ticketId}`);
      toast.success('Tanda eliminada');
      loadTickets();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleToggleVisible = async (ticket) => {
    const next = !(ticket.is_visible === false ? false : true);
    // Optimistic update for snappy UX
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, is_visible: next } : t));
    try {
      await api.put(`/producers/me/events/${eventId}/tickets/${ticket.id}`, { is_visible: next });
      toast.success(next ? 'Tanda visible en la venta' : 'Tanda oculta de la venta');
      loadTickets();
    } catch (error) {
      console.error('Error:', error);
      toast.error('No se pudo cambiar la visibilidad');
      loadTickets();
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-white">Tandas de Entradas</h3>
        {isEditable && (
          <Button onClick={openNewModal} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white">
            <Plus size={16} className="mr-2" />
            Nueva Tanda
          </Button>
        )}
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Ticket size={48} className="mx-auto mb-4 text-zinc-700" />
          <p>No hay entradas configuradas</p>
          {isEditable && (
            <Button onClick={openNewModal} className="mt-4 bg-gradient-to-r from-violet-600 to-pink-600 text-white">
              <Plus size={16} className="mr-2" />
              Crear primera tanda
            </Button>
          )}
        </div>
      ) : (
        // Sort: visible+available first → visible+sold-out → hidden last
        [...tickets].sort((a, b) => {
          const hiddenA = a.is_visible === false ? 1 : 0;
          const hiddenB = b.is_visible === false ? 1 : 0;
          if (hiddenA !== hiddenB) return hiddenA - hiddenB;
          const soldOutA = (a.is_sold_out || ((a.quantity || 0) > 0 && (a.sold || 0) >= (a.quantity || 0))) ? 1 : 0;
          const soldOutB = (b.is_sold_out || ((b.quantity || 0) > 0 && (b.sold || 0) >= (b.quantity || 0))) ? 1 : 0;
          if (soldOutA !== soldOutB) return soldOutA - soldOutB;
          return 0;
        }).map((ticket, i) => {
          const isHidden = ticket.is_visible === false;
          const isSoldOut = ticket.is_sold_out || ((ticket.quantity || 0) > 0 && (ticket.sold || 0) >= (ticket.quantity || 0));
          const pct = (ticket.quantity || 0) > 0
            ? Math.min(100, Math.round(((ticket.sold || 0) / ticket.quantity) * 100))
            : 0;
          const lastSold = ticket.last_sold_at ? new Date(ticket.last_sold_at) : null;
          const isHot = lastSold && (Date.now() - lastSold.getTime()) < 10 * 60 * 1000;
          return (
          <div
            key={ticket.id || i}
            className={`bg-zinc-900 border rounded-xl p-4 transition-colors cursor-pointer ${isHidden ? 'border-zinc-800/50 opacity-60' : 'border-zinc-800 hover:border-zinc-700'}`}
            onClick={() => isEditable && openEditModal(ticket)}
            data-testid={`producer-tier-card-${ticket.id || i}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold text-white">{ticket.name}</h4>
                  {ticket.promo_pay_x > 0 && ticket.promo_get_y > ticket.promo_pay_x && (
                    <span className="px-2 py-0.5 bg-[#D4AF37] text-black text-xs rounded font-black">
                      {ticket.promo_pay_x}x{ticket.promo_get_y}
                    </span>
                  )}
                  {isHot && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 text-emerald-300 text-[10px] font-bold uppercase tracking-wider rounded-full border border-emerald-500/30"
                      title={`Última venta: ${lastSold.toLocaleString('es-AR')}`}
                      data-testid={`tier-hot-${ticket.id || i}`}
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                      </span>
                      En vivo
                    </span>
                  )}
                  {ticket.free_for_bravx && (
                    <span className="px-2 py-0.5 bg-violet-500/20 text-violet-500 text-xs rounded">FREE BRAVX</span>
                  )}
                  {isHidden && (
                    <span className="px-2 py-0.5 bg-zinc-700/50 text-zinc-300 text-xs rounded uppercase tracking-wide">Oculta</span>
                  )}
                  {isSoldOut && !isHidden && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded uppercase tracking-wide">Agotado</span>
                  )}
                  {!ticket.is_active && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Inactiva</span>
                  )}
                </div>
                <p className="text-sm text-zinc-400 mt-0.5">
                  ${ticket.price} · Stock: <span className="text-white font-medium">{ticket.sold || 0}/{ticket.quantity}</span> <span className="text-zinc-500">({pct}%)</span> · Max: {ticket.max_per_order}/orden
                </p>
                {/* Gold progress bar */}
                <div className="mt-2 h-1.5 bg-zinc-800 rounded-full overflow-hidden" data-testid={`tier-progress-${ticket.id || i}`}>
                  <div
                    className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] transition-[width] duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {ticket.discount_code && (
                  <p className="text-xs text-green-400 mt-1.5">
                    Código: {ticket.discount_code} (-{ticket.discount_percent}%)
                  </p>
                )}
              </div>
              {isEditable && (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleVisible(ticket)}
                    className={isHidden ? 'text-zinc-500 hover:text-white' : 'text-violet-400 hover:text-violet-300'}
                    title={isHidden ? 'Mostrar en la venta' : 'Ocultar de la venta'}
                    data-testid={`tier-toggle-visible-${ticket.id || i}`}
                  >
                    {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(ticket)} className="text-zinc-400 hover:text-white">
                    <Edit2 size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(ticket.id)} className="text-red-400 hover:text-red-300">
                    <Trash2 size={16} />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );})
      )}

      {/* Ticket Modal - Full form like event creation */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full p-6 my-8"
            >
              <h3 className="text-xl font-bold text-white mb-4">
                {editingTicket ? 'Editar Tanda' : 'Nueva Tanda de Entradas'}
              </h3>
              
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Tier Type */}
                <div>
                  <Label className="text-zinc-400 mb-2 block">Tipo de tanda</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="tier-type-picker">
                    {[
                      { id: 'sale', label: 'Venta online', desc: 'Compra directa con MP', icon: '🛒' },
                      { id: 'inquiry', label: 'Consultar', desc: 'Redirige a WhatsApp/DM', icon: '💬' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`tier-type-${opt.id}`}
                        onClick={() => setTicketForm(prev => ({ ...prev, tier_type: opt.id }))}
                        className={`p-3 rounded-lg border text-left transition-all ${ticketForm.tier_type === opt.id ? 'border-violet-500 bg-violet-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-lg">{opt.icon}</span>
                          <span className="text-sm text-white font-medium">{opt.label}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-400">Nombre de la tanda *</Label>
                  <Input
                    value={ticketForm.name}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={ticketForm.tier_type === 'inquiry' ? 'Ej: Mesa VIP (8 personas)' : 'Ej: Early Bird, General, VIP'}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>

                {/* Inquiry-only fields */}
                {ticketForm.tier_type === 'inquiry' && (
                  <div className="space-y-3 p-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5" data-testid="inquiry-block">
                    <p className="text-xs text-cyan-200 leading-relaxed">
                      Esta opción no se vende online. Cuando un cliente la elija, se inicia un chat <strong>dentro de la plataforma</strong> entre él y vos (sin redirecciones externas). Las consultas aparecen en el tab <strong>"Consultas"</strong> de este mismo evento.
                    </p>
                    <div>
                      <Label className="text-zinc-400">Texto del botón (opcional)</Label>
                      <Input
                        value={ticketForm.inquiry_label}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, inquiry_label: e.target.value }))}
                        placeholder="Ej: Consultar disponibilidad"
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                      <p className="text-[10px] text-zinc-500 mt-1">Si lo dejás vacío, decimos "Iniciar consulta".</p>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded-md hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={!!ticketForm.is_sold_out}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, is_sold_out: e.target.checked }))}
                        className="mt-0.5 accent-emerald-500"
                        data-testid="inquiry-soldout-toggle"
                      />
                      <div>
                        <p className="text-xs text-white font-medium">Marcar como agotada</p>
                        <p className="text-[10px] text-zinc-400 leading-snug">Si ya vendiste todas estas mesas/cupos, activá esto para que aparezca como "Agotado" en el listado público y no acepte más consultas.</p>
                      </div>
                    </label>
                  </div>
                )}

                {ticketForm.tier_type === 'sale' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Precio ($)</Label>
                    <Input
                      type="number"
                      value={ticketForm.price}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      min={0}
                      className="bg-zinc-800 border-zinc-700 mt-1"
                      disabled={ticketForm.price_stages?.length > 0}
                    />
                    {ticketForm.price_stages?.length > 0 && (
                      <p className="text-[10px] text-violet-400 mt-1">Usando precio por etapas ↓</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-zinc-400">Cantidad disponible</Label>
                    <Input
                      type="number"
                      value={ticketForm.quantity}
                      onChange={(e) => setTicketForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                      min={1}
                      className="bg-zinc-800 border-zinc-700 mt-1"
                    />
                  </div>
                </div>)}

                <div>
                  <Label className="text-zinc-400">Máximo por orden</Label>
                  <Input
                    type="number"
                    value={ticketForm.max_per_order}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, max_per_order: parseInt(e.target.value) || 1 }))}
                    min={1}
                    max={20}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>

                <div>
                  <Label className="text-zinc-400">Descripción (opcional)</Label>
                  <Textarea
                    value={ticketForm.description}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Beneficios incluidos..."
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    rows={2}
                  />
                </div>

                {/* Multi-stage Pricing (only for sale-type tiers) */}
                {ticketForm.tier_type === 'sale' && (
                  <div className="p-3 rounded-lg border border-violet-500/30 bg-violet-500/5 space-y-2" data-testid="price-stages-block">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-violet-200 font-medium flex items-center gap-1.5">
                          🎯 Precios por etapas
                          <span className="text-[9px] uppercase tracking-wider text-violet-400 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5">Opcional</span>
                        </div>
                        <p className="text-[10px] text-zinc-400 mt-0.5 leading-snug">Cuando se agota una tanda, se activa la siguiente automáticamente.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTicketForm(prev => ({
                          ...prev,
                          price_stages: [...(prev.price_stages || []), { label: `Etapa ${(prev.price_stages?.length || 0) + 1}`, price: prev.price || 0, quantity: 0, starts_at: '', ends_at: '' }]
                        }))}
                        className="text-xs px-2 py-1 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-medium"
                        data-testid="add-stage-btn"
                      >
                        + Etapa
                      </button>
                    </div>
                    {(ticketForm.price_stages || []).map((stage, idx) => (
                      <div key={idx} className="rounded-md border border-zinc-700 bg-zinc-900/60 p-2 space-y-1.5" data-testid={`stage-${idx}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase text-violet-300 font-bold bg-violet-500/15 rounded px-1.5 py-0.5">#{idx + 1}</span>
                          <Input
                            value={stage.label || ''}
                            onChange={(e) => setTicketForm(prev => {
                              const stages = [...prev.price_stages];
                              stages[idx] = { ...stages[idx], label: e.target.value };
                              return { ...prev, price_stages: stages };
                            })}
                            placeholder={`Ej: Preventa, Segunda tanda, Última`}
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => setTicketForm(prev => ({ ...prev, price_stages: prev.price_stages.filter((_, i) => i !== idx) }))}
                            className="text-red-400 hover:text-red-300 text-xs px-1"
                            data-testid={`remove-stage-${idx}`}
                          >✕</button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Input
                            type="number"
                            value={stage.price || 0}
                            onChange={(e) => setTicketForm(prev => {
                              const stages = [...prev.price_stages];
                              stages[idx] = { ...stages[idx], price: parseFloat(e.target.value) || 0 };
                              return { ...prev, price_stages: stages };
                            })}
                            placeholder="Precio $"
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                            data-testid={`stage-price-${idx}`}
                          />
                          <Input
                            type="number"
                            value={stage.quantity || 0}
                            onChange={(e) => setTicketForm(prev => {
                              const stages = [...prev.price_stages];
                              stages[idx] = { ...stages[idx], quantity: parseInt(e.target.value) || 0 };
                              return { ...prev, price_stages: stages };
                            })}
                            placeholder="Cantidad"
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                            data-testid={`stage-qty-${idx}`}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <Input
                            type="datetime-local"
                            value={stage.starts_at ? stage.starts_at.slice(0, 16) : ''}
                            onChange={(e) => setTicketForm(prev => {
                              const stages = [...prev.price_stages];
                              stages[idx] = { ...stages[idx], starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' };
                              return { ...prev, price_stages: stages };
                            })}
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                            title="Inicio de la etapa (opcional)"
                          />
                          <Input
                            type="datetime-local"
                            value={stage.ends_at ? stage.ends_at.slice(0, 16) : ''}
                            onChange={(e) => setTicketForm(prev => {
                              const stages = [...prev.price_stages];
                              stages[idx] = { ...stages[idx], ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' };
                              return { ...prev, price_stages: stages };
                            })}
                            className="bg-zinc-800 border-zinc-700 h-7 text-xs"
                            title="Fin de la etapa (opcional)"
                          />
                        </div>
                      </div>
                    ))}
                    {(ticketForm.price_stages?.length || 0) === 0 && (
                      <p className="text-[10px] text-zinc-500 italic text-center py-2">Sin etapas — se usa el precio único de arriba.</p>
                    )}
                  </div>
                )}

                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-sm text-zinc-500 mb-3">Opciones avanzadas</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">Fin de venta</Label>
                      <Input
                        type="datetime-local"
                        value={ticketForm.sale_end_date}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, sale_end_date: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Límite de ingreso</Label>
                      <Input
                        type="time"
                        value={ticketForm.entry_limit_time}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, entry_limit_time: e.target.value }))}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                  </div>

                  {ticketForm.tier_type === 'sale' && (
                    <div className="mt-4 bg-[#D4AF37]/5 border border-[#D4AF37]/25 rounded-lg p-3">
                      <p className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5"><Gift size={12} /> Promo "Pagás X, llevás Y" (2x1)</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-zinc-400 text-xs">Pagás</Label>
                          <Input type="number" min="0" value={ticketForm.promo_pay_x} onChange={(e) => setTicketForm(prev => ({ ...prev, promo_pay_x: Number(e.target.value) }))} className="bg-zinc-800 border-zinc-700 mt-1" data-testid="tier-promo-pay-x" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Llevás</Label>
                          <Input type="number" min="0" value={ticketForm.promo_get_y} onChange={(e) => setTicketForm(prev => ({ ...prev, promo_get_y: Number(e.target.value) }))} className="bg-zinc-800 border-zinc-700 mt-1" data-testid="tier-promo-get-y" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">Válida hasta</Label>
                          <Input type="datetime-local" value={ticketForm.promo_valid_until} onChange={(e) => setTicketForm(prev => ({ ...prev, promo_valid_until: e.target.value }))} className="bg-zinc-800 border-zinc-700 mt-1" />
                        </div>
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-2">Ej: 2x1 = pagás 1, llevás 2. Las entradas de regalo figuran en el reporte como descuento de promo. Dejá en 0 para desactivar.</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-4 mt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ticketForm.is_visible}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, is_visible: e.target.checked }))}
                        className="rounded border-zinc-700"
                      />
                      <span className="text-sm text-zinc-400">Visible en página</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ticketForm.is_active}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded border-zinc-700"
                      />
                      <span className="text-sm text-zinc-400">Activa para venta</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-zinc-400">Código descuento</Label>
                      <Input
                        value={ticketForm.discount_code}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, discount_code: e.target.value.toUpperCase() }))}
                        placeholder="PROMO20"
                        className="bg-zinc-800 border-zinc-700 mt-1 font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">% Descuento</Label>
                      <Input
                        type="number"
                        value={ticketForm.discount_percent}
                        onChange={(e) => setTicketForm(prev => ({ ...prev, discount_percent: parseInt(e.target.value) || 0 }))}
                        min={0}
                        max={100}
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-zinc-800">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-zinc-700">
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 text-white">
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  {editingTicket ? 'Guardar Cambios' : 'Crear Tanda'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Orders Tab
const OrdersTab = ({ eventId }) => {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(null); // aggregate over ALL orders
  const [pageInfo, setPageInfo] = useState({ has_more: false, returned: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  // NEW: State for order details modal with individual tickets
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [orderTickets, setOrderTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  useEffect(() => {
    loadOrders();
    // Auto-refresh orders every 30 seconds
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = async (opts = {}) => {
    try {
      const skip = opts.skip || 0;
      const response = await api.get(`/producers/me/events/${eventId}/orders`, { params: { skip, limit: 50 } });
      const data = response.data;
      // New shape: { orders, totals, page }. Fallback for legacy array shape.
      if (Array.isArray(data)) {
        setOrders(data);
        setTotals(null);
        setPageInfo({ has_more: false, returned: data.length });
      } else {
        const newOrders = data.orders || [];
        setOrders(prev => skip > 0 ? [...prev, ...newOrders] : newOrders);
        setTotals(data.totals || null);
        setPageInfo(data.page || { has_more: false, returned: newOrders.length });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    await loadOrders({ skip: orders.length });
  };

  // NEW: Load individual tickets for an order
  const loadOrderTickets = async (orderId) => {
    setTicketsLoading(true);
    try {
      const response = await api.get(`/producers/me/events/${eventId}/orders/${orderId}/tickets`);
      setOrderTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
      setOrderTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  // NEW: Open order details modal
  const handleOpenOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderTickets([]);
    setShowOrderDetails(true);
    loadOrderTickets(order.id);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/producers/me/events/${eventId}/orders/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compradores_${eventId}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado correctamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar datos');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-white">Órdenes de compra</h3>
          <span className="text-sm text-zinc-500">
            {totals
              ? <>Mostrando {orders.length} de {totals.total_orders} orden(es)</>
              : <>{orders.length} orden(es)</>
            }
          </span>
        </div>
        {orders.length > 0 && (
          <Button
            onClick={handleExportExcel}
            disabled={exporting}
            variant="outline"
            className="border-violet-500 text-violet-500 hover:bg-violet-500/10"
            data-testid="export-orders-btn"
          >
            {exporting ? (
              <Loader2 size={16} className="animate-spin mr-2" />
            ) : (
              <Download size={16} className="mr-2" />
            )}
            Descargar Excel
          </Button>
        )}
      </div>
      {orders.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <ShoppingCart size={48} className="mx-auto mb-4 text-zinc-700" />
          <p>No hay órdenes aún</p>
        </div>
      ) : (
        <>
          {/* Stats Summary */}
          {(() => {
            // Prefer backend aggregate (covers ALL orders, not just the paginated page)
            const t = totals || {};
            const totalOrders = totals ? t.total_orders : orders.length;
            const totalTickets = totals
              ? t.total_tickets
              : orders.reduce((s, o) => s + (o.total_tickets || o.quantity || 0), 0);
            const netRevenue = totals
              ? t.total_net
              : orders.reduce((s, o) => {
                  const sub = o.subtotal || ((o.unit_price || 0) * (o.quantity || 1));
                  const disc = o.discount_amount || 0;
                  const net = (o.net_amount !== undefined && o.net_amount !== null)
                    ? o.net_amount
                    : Math.max(0, sub - disc);
                  return s + net;
                }, 0);
            const discountTotal = totals
              ? t.total_discount
              : orders.reduce((s, o) => s + (o.discount_amount || 0), 0);
            const totalRevenue = totals
              ? t.total_paid
              : orders.reduce((s, o) => s + (o.total_paid || o.total_amount || 0), 0);
            const showDiscountCard = discountTotal > 0;
            return (
              <div className={`grid grid-cols-2 ${showDiscountCard ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3 mb-4`}>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Total Órdenes</p>
                  <p className="text-2xl font-black text-white">{totalOrders}</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Tickets Vendidos</p>
                  <p className="text-2xl font-black text-white">{totalTickets}</p>
                </div>
                <div className="bg-zinc-900 border border-emerald-500/30 rounded-xl p-4" data-testid="producer-orders-net-revenue-card">
                  <p className="text-emerald-400 text-xs uppercase tracking-wider">Ingreso Neto</p>
                  <p className="text-2xl font-black text-emerald-400">${netRevenue.toLocaleString()}</p>
                  <p className="text-[10px] text-zinc-500">Descuentos restados</p>
                </div>
                {showDiscountCard && (
                  <div className="bg-zinc-900 border border-amber-500/30 rounded-xl p-4" data-testid="producer-orders-discount-card">
                    <p className="text-amber-400 text-xs uppercase tracking-wider">Descuentos Aplicados</p>
                    <p className="text-2xl font-black text-amber-400">−${discountTotal.toLocaleString()}</p>
                    <p className="text-[10px] text-zinc-500">Cupones canjeados</p>
                  </div>
                )}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider">Total Recaudado</p>
                  <p className="text-2xl font-black text-white">${totalRevenue.toLocaleString()}</p>
                  <p className="text-[10px] text-zinc-500">Con costos de servicio</p>
                </div>
              </div>
            );
          })()}

          <div className="space-y-3">
          {orders.map((order, i) => (
            <div key={order.id || i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`producer-order-row-${order.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-white">{order.buyer_name}</h4>
                    {(order.discount_amount || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-wider"
                        data-testid={`producer-order-discount-badge-${order.id}`}
                      >
                        <Tag size={10} />
                        Descuento {order.discount_code ? `· ${order.discount_code}` : ''}
                      </span>
                    )}
                    {(order.bonus_quantity || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-wider"
                        data-testid={`producer-order-promo-badge-${order.id}`}
                        title={`Pagó ${order.quantity} y recibió ${order.total_tickets || order.quantity + order.bonus_quantity} entradas`}
                      >
                        <Gift size={10} />
                        Promo {order.promo_applied?.pay_x && order.promo_applied?.get_y ? `${order.promo_applied.get_y}x${order.promo_applied.pay_x}` : ''} · +{order.bonus_quantity} gratis
                      </span>
                    )}
                    {order.bravx_referral_username && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 text-[10px] font-bold uppercase tracking-wider"
                        data-testid={`producer-order-bravx-badge-${order.id}`}
                        title={`Compra apoyada por @${order.bravx_referral_username}`}
                      >
                        <Sparkles size={10} />
                        BRAVX · @{order.bravx_referral_username}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400">{order.buyer_email}</p>
                  {order.buyer_phone && (
                    <p className="text-xs text-zinc-500">{order.buyer_phone}</p>
                  )}
                </div>
                <div className="text-right">
                  {(order.discount_amount || 0) > 0 ? (
                    <>
                      <p className="text-[10px] text-zinc-600 line-through" data-testid={`order-subtotal-${order.id}`}>
                        ${(order.subtotal || 0).toLocaleString()}
                      </p>
                      <p className="font-bold text-violet-500 text-lg" data-testid={`order-net-${order.id}`}>
                        ${(order.net_amount || 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-amber-400" data-testid={`order-discount-${order.id}`}>
                        {order.discount_code ? `${order.discount_code}: ` : ''}−${(order.discount_amount || 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-zinc-500">Ganancia neta</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-violet-500 text-lg" data-testid={`order-net-${order.id}`}>
                        ${(order.net_amount || 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-zinc-500">Ganancia neta</p>
                    </>
                  )}
                  {order.service_fee > 0 && (
                    <p className="text-[10px] text-zinc-600">Service: ${order.service_fee?.toLocaleString()}</p>
                  )}
                </div>
              </div>
              
              {/* Detalles de la entrada */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-zinc-800">
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Tanda</p>
                  <p className="text-sm text-white font-medium">{order.tier_name || 'General'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Cantidad</p>
                  {(order.bonus_quantity || 0) > 0 ? (
                    <p className="text-sm text-white font-medium" data-testid={`producer-order-qty-${order.id}`}>
                      {order.total_tickets || order.quantity + order.bonus_quantity} entradas{' '}
                      <span className="text-emerald-400 text-xs">({order.quantity} paga{order.quantity > 1 ? 's' : ''} + {order.bonus_quantity} gratis)</span>
                    </p>
                  ) : (
                    <p className="text-sm text-white font-medium">{order.quantity} entrada(s)</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">DNI Comprador</p>
                  <p className="text-sm text-white font-mono">{order.buyer_dni || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Fecha</p>
                  <p className="text-sm text-white">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString('es-AR', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </p>
                </div>
              </div>

              {/* Discount info banner */}
              {(order.discount_amount || 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs bg-amber-500/5 border border-amber-500/20 rounded px-3 py-2">
                  <span className="text-amber-400">
                    Código <span className="font-mono font-semibold">{order.discount_code || 'DESCUENTO'}</span> aplicado
                  </span>
                  <span className="text-amber-400">
                    ${(order.subtotal || 0).toLocaleString()} − ${(order.discount_amount || 0).toLocaleString()} = <span className="font-bold">${(order.net_amount || 0).toLocaleString()}</span>
                  </span>
                </div>
              )}

              {/* Promo "pay X get Y" info banner */}
              {(order.bonus_quantity || 0) > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs bg-emerald-500/5 border border-emerald-500/20 rounded px-3 py-2" data-testid={`producer-order-promo-banner-${order.id}`}>
                  <span className="text-emerald-400">
                    <Gift size={11} className="inline mr-1 -mt-0.5" />
                    Promo {order.promo_applied?.pay_x && order.promo_applied?.get_y ? `${order.promo_applied.get_y}x${order.promo_applied.pay_x}` : ''}: pagó {order.quantity}, recibió <span className="font-bold">{order.total_tickets || order.quantity + order.bonus_quantity}</span> entradas
                  </span>
                  {(order.promo_applied?.amount_saved || 0) > 0 && (
                    <span className="text-emerald-400">Ahorró ${(order.promo_applied.amount_saved).toLocaleString()}</span>
                  )}
                </div>
              )}

              {/* Ver detalles button */}
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-400 hover:text-white"
                  onClick={() => handleOpenOrderDetails(order)}
                  data-testid={`producer-order-view-details-${order.id}`}
                >
                  <Eye size={14} className="mr-2" />
                  Ver detalles {(order.total_tickets || order.quantity) > 1 ? `(${order.total_tickets || order.quantity} entradas)` : ''}
                </Button>
              </div>
            </div>
          ))}
          {/* Load more pagination */}
          {pageInfo?.has_more && (
            <div className="pt-2 flex justify-center">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="producer-orders-load-more"
              >
                {loadingMore ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Cargar más ({(totals?.total_orders || 0) - orders.length} restantes)
              </Button>
            </div>
          )}
        </div>
        </>
      )}

      {/* Order Details Modal */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ticket size={20} className="text-violet-500" />
              Entradas de la Orden
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 py-2">
              {/* Buyer info summary */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Comprador</p>
                <p className="font-medium text-white">{selectedOrder.buyer_name}</p>
                <p className="text-sm text-zinc-400">{selectedOrder.buyer_email}</p>
              </div>

              {/* Price breakdown */}
              <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2" data-testid="producer-order-details-breakdown">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Desglose de precio</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Subtotal ({selectedOrder.quantity} × ${(selectedOrder.unit_price || 0).toLocaleString()})</span>
                  <span className="font-mono text-white">${(selectedOrder.subtotal || 0).toLocaleString()}</span>
                </div>
                {(selectedOrder.discount_amount || 0) > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm" data-testid="producer-order-details-discount-row">
                      <span className="text-amber-400 flex items-center gap-1.5">
                        <Tag size={12} />
                        Descuento
                        {selectedOrder.discount_code && (
                          <span className="font-mono bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded text-[10px]">
                            {selectedOrder.discount_code}
                          </span>
                        )}
                      </span>
                      <span className="font-mono text-amber-400">−${(selectedOrder.discount_amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-1 border-t border-zinc-700/50">
                      <span className="text-zinc-300">Ganancia neta productora</span>
                      <span className="font-mono font-bold text-violet-400">${(selectedOrder.net_amount || 0).toLocaleString()}</span>
                    </div>
                  </>
                )}
                {(selectedOrder.service_fee || 0) > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-500">Cargo de servicio</span>
                    <span className="font-mono text-zinc-400">${(selectedOrder.service_fee || 0).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm pt-2 border-t border-zinc-700">
                  <span className="text-zinc-300 font-semibold">Total pagado por el cliente</span>
                  <span className="font-mono font-bold text-white">${(selectedOrder.total_paid || selectedOrder.total_amount || 0).toLocaleString()}</span>
                </div>
              </div>

              {/* Individual tickets */}
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">
                  {selectedOrder.quantity} Entrada(s) Individual(es)
                </p>
                
                {ticketsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="animate-spin text-violet-500" />
                  </div>
                ) : orderTickets.length > 0 ? (
                  <div className="space-y-2">
                    {orderTickets.map((ticket, index) => (
                      <div 
                        key={ticket.id} 
                        className={`p-3 rounded-lg border ${
                          ticket.checked_in 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-zinc-800/50 border-zinc-700'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                              ticket.checked_in ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-zinc-500">{ticket.ticket_code}</span>
                                {ticket.tier_name && <span className="text-zinc-400">• {ticket.tier_name}</span>}
                                {ticket.checked_in && (
                                  <span className="text-green-400 flex items-center gap-1">
                                    <CheckCircle size={12} /> Ingresado
                                  </span>
                                )}
                              </p>
                              
                              {/* DNI Display - Read Only for Producers */}
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-zinc-500">
                                  DNI Titular: <span className="font-mono text-zinc-300">{ticket.holder_dni || 'No asignado'}</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-sm text-center py-4">No se encontraron entradas</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reports Tab
const ReportsTab = ({ eventId }) => {
  const [stats, setStats] = useState(null);
  const [fullReport, setFullReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showFullReport, setShowFullReport] = useState(false);

  useEffect(() => {
    loadStats();
    loadReportHistory();
    // Auto-refresh stats every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReportHistory = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/report/history`);
      setReportHistory(response.data);
    } catch (error) {
      console.error('Error loading report history:', error);
    }
  };

  const loadFullReport = async () => {
    try {
      setShowFullReport(true);
      const response = await api.get(`/producers/me/events/${eventId}/report`);
      setFullReport(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Error al cargar el reporte completo');
    }
  };

  const sendReportEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await api.post(`/producers/me/events/${eventId}/report/send`);
      toast.success(response.data.message || 'Reporte enviado por email');
      // Reload history after sending
      loadReportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el reporte');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-white">Reportes del Evento</h3>
        <div className="flex gap-2">
          <Button
            onClick={loadFullReport}
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-800"
          >
            <BarChart3 size={16} className="mr-2" />
            Ver Reporte Completo
          </Button>
          <Button
            onClick={sendReportEmail}
            disabled={sendingEmail}
            className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
          >
            {sendingEmail ? (
              <Loader2 size={16} className="animate-spin mr-2" />
            ) : (
              <Send size={16} className="mr-2" />
            )}
            Enviar por Email
          </Button>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <p className="text-zinc-400 text-sm">Entradas Vendidas</p>
          <p className="text-3xl font-bold text-white mt-2">{stats?.tickets_sold || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <p className="text-zinc-400 text-sm">Ganancia Neta</p>
          <p className="text-3xl font-bold text-green-400 mt-2">${(stats?.total_revenue || 0).toLocaleString()}</p>
          <p className="text-xs text-zinc-600 mt-1">Sin cargo por servicio</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <p className="text-zinc-400 text-sm">Check-ins</p>
          <p className="text-3xl font-bold text-violet-500 mt-2">{stats?.check_ins || 0}</p>
        </div>
      </div>

      {/* Report History */}
      {reportHistory.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h4 className="font-bold text-white mb-4 flex items-center gap-2">
            <Clock size={16} className="text-zinc-500" />
            Historial de Reportes Enviados
          </h4>
          <div className="space-y-2">
            {reportHistory.map((report) => (
              <div key={report.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${report.type === 'automatic' ? 'bg-blue-500' : 'bg-violet-500'}`} />
                  <div>
                    <p className="text-white text-sm">
                      Enviado a <span className="text-zinc-400">{report.sent_to}</span>
                    </p>
                    <p className="text-zinc-500 text-xs">
                      {new Date(report.sent_at).toLocaleDateString('es-AR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {' · '}
                      {report.type === 'automatic' ? 'Automático' : 'Manual'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-bold">${(report.summary?.net_revenue || 0).toLocaleString()}</p>
                  <p className="text-zinc-500 text-xs">{report.summary?.total_tickets || 0} entradas</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full Report Modal */}
      <Dialog open={showFullReport} onOpenChange={setShowFullReport}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BarChart3 className="text-violet-500" />
              Reporte Completo del Evento
            </DialogTitle>
          </DialogHeader>
          
          {fullReport ? (
            <div className="space-y-6 py-4">
              {/* Event Info */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="font-bold text-white text-lg">{fullReport.event?.title}</h4>
                <p className="text-zinc-400 text-sm mt-1">
                  📍 {fullReport.event?.location || 'Sin ubicación'}
                </p>
              </div>

              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-white">{fullReport.summary?.total_tickets_sold || 0}</p>
                  <p className="text-xs text-zinc-500 uppercase">Vendidas</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">{fullReport.summary?.checked_in || 0}</p>
                  <p className="text-xs text-zinc-500 uppercase">Asistieron</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-400">{fullReport.summary?.not_checked_in || 0}</p>
                  <p className="text-xs text-zinc-500 uppercase">No Asistieron</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-violet-500">{fullReport.summary?.attendance_rate || 0}%</p>
                  <p className="text-xs text-zinc-500 uppercase">Asistencia</p>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gradient-to-r from-zinc-800 to-zinc-800/50 border border-violet-500/30 rounded-lg p-4">
                <h4 className="font-bold text-violet-500 text-sm uppercase tracking-wider mb-3">💰 Resumen Financiero</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Venta de entradas:</span>
                    <span className="text-white font-bold" data-testid="report-total-revenue">${(fullReport.summary?.total_revenue || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Cargo por servicio (lo paga el cliente):</span>
                    <span className="text-zinc-500">${(fullReport.summary?.service_fees || 0).toLocaleString()}</span>
                  </div>
                  {(fullReport.summary?.discount_total || 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-amber-400">− Descuentos aplicados (códigos):</span>
                      <span className="text-amber-400" data-testid="report-discount-total">−${(fullReport.summary?.discount_total || 0).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-zinc-700 pt-2 mt-2">
                    <span className="text-white font-bold">Tu Recaudación Neta:</span>
                    <span className="text-green-400 font-bold text-xl" data-testid="report-net-revenue">${(fullReport.summary?.net_revenue || 0).toLocaleString()}</span>
                  </div>
                  {(fullReport.promo_2x1?.free_units || 0) > 0 && (
                    <div className="flex justify-between mt-1">
                      <span className="text-[#D4AF37]">🎁 Promo 2x1: {fullReport.promo_2x1.free_units} entrada{fullReport.promo_2x1.free_units > 1 ? 's' : ''} de regalo ({fullReport.promo_2x1.orders_count} orden{fullReport.promo_2x1.orders_count > 1 ? 'es' : ''}):</span>
                      <span className="text-[#D4AF37]" data-testid="report-promo-2x1">−${(fullReport.promo_2x1.amount_saved || 0).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <p className="text-zinc-500 text-xs mt-3">
                  * El cargo por servicio es abonado por el cliente y corresponde a la plataforma.
                  {(fullReport.summary?.discount_total || 0) > 0 && ' Los descuentos por código se restan de tu recaudación neta.'}
                </p>
              </div>

              {/* Tickets by Tier */}
              {fullReport.tickets_by_tier && Object.keys(fullReport.tickets_by_tier).length > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-3">🎫 Ventas por Tipo</h4>
                  <div className="space-y-2">
                    {Object.entries(fullReport.tickets_by_tier).map(([tier, count]) => (
                      <div key={tier} className="flex justify-between items-center">
                        <span className="text-zinc-400">{tier}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-white font-mono">{count} tickets</span>
                          <span className="text-violet-500 font-mono">
                            ${(fullReport.revenue_by_tier?.[tier] || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Check-in Methods */}
              {fullReport.checkin_methods && Object.keys(fullReport.checkin_methods).length > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-3">📱 Métodos de Check-in</h4>
                  <div className="flex gap-4">
                    {Object.entries(fullReport.checkin_methods).map(([method, count]) => (
                      <div key={method} className="flex items-center gap-2">
                        <span className="text-zinc-400 capitalize">
                          {method === 'qr' ? 'Código QR' : method === 'dni' ? 'DNI' : method}:
                        </span>
                        <span className="text-white font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invitations */}
              {fullReport.invitations?.total > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-3">🎁 Invitaciones</h4>
                  <div className="flex gap-6">
                    <div>
                      <span className="text-zinc-400">Total:</span>
                      <span className="text-white font-bold ml-2">{fullReport.invitations.total}</span>
                    </div>
                    <div>
                      <span className="text-zinc-400">Usadas:</span>
                      <span className="text-green-400 font-bold ml-2">{fullReport.invitations.used}</span>
                    </div>
                    <div>
                      <span className="text-zinc-400">Sin usar:</span>
                      <span className="text-red-400 font-bold ml-2">{fullReport.invitations.unused}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Buyers */}
              {fullReport.top_buyers && fullReport.top_buyers.length > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-3">👥 Top Compradores</h4>
                  <div className="space-y-2">
                    {fullReport.top_buyers.slice(0, 5).map(([email, total], index) => (
                      <div key={email} className="flex justify-between items-center">
                        <span className="text-zinc-400">
                          {index + 1}. {email.substring(0, 3)}***{email.substring(email.indexOf('@'))}
                        </span>
                        <span className="text-violet-500 font-mono">${total.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Settlement Notice */}
              {fullReport?.summary?.net_revenue > 0 && (
                <div className="bg-gradient-to-r from-yellow-900/30 to-zinc-800/50 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="font-bold text-yellow-500 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                    💳 Liquidación de Fondos
                  </h4>
                  <p className="text-yellow-200 text-sm">
                    Tu recaudación neta (<span className="font-bold">${(fullReport.summary.net_revenue || 0).toLocaleString()}</span>) será transferida a tu cuenta bancaria dentro de los <span className="font-bold">5 días hábiles</span> posteriores al evento.
                  </p>
                  <p className="text-zinc-400 text-xs mt-2">
                    Asegurate de tener tus datos bancarios actualizados en Configuración.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-violet-500" size={32} />
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={sendReportEmail}
              disabled={sendingEmail}
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
            >
              {sendingEmail ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              Enviar Reporte por Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Lineup Tab
const LineupTab = ({ eventId, isEditable }) => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [saving, setSaving] = useState(false);
  // DJ picker (link existing platform DJ)
  const [showDJPicker, setShowDJPicker] = useState(false);
  const [djList, setDjList] = useState([]);
  const [djLoading, setDjLoading] = useState(false);
  const [djQuery, setDjQuery] = useState('');
  const [linkedDjId, setLinkedDjId] = useState(null);  // tracks if current form was prefilled from a DJ
  const [hlChoice, setHlChoice] = useState('none');  // none | headliner | guest | custom
  const [newArtist, setNewArtist] = useState({
    name: '',
    role: 'DJ',
    image_url: '',
    start_time: '',
    end_time: '',
    stage: '',
    is_headliner: false,
    headliner_label: '',
    press_kit_email: '',
    b2b: false,
    b2b_artist: { name: '', image_url: '', dj_profile_id: null, dj_slug: null },
    bio: '',
    social_links: { instagram: '', spotify: '' }
  });

  const buildHighlightFields = () => ({
    is_headliner: hlChoice !== 'none',
    headliner_label: hlChoice === 'guest' ? 'Invitado Especial'
      : hlChoice === 'custom' ? (newArtist.headliner_label || '').trim()
      : hlChoice === 'headliner' ? 'Headliner' : '',
  });

  useEffect(() => { loadLineup(); }, []);

  const loadLineup = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/lineup`);
      setArtists(response.data || []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const buildB2bFields = () => {
    const hasB2b = !!newArtist.b2b && !!(newArtist.b2b_artist?.name || '').trim();
    return { b2b: hasB2b, b2b_artist: hasB2b ? newArtist.b2b_artist : null };
  };

  const handleCreate = async () => {
    if (!newArtist.name.trim()) { toast.error('Ingresa el nombre del artista'); return; }
    if (newArtist.b2b && !(newArtist.b2b_artist?.name || '').trim()) {
      toast.error('Ingresá el nombre del segundo artista del B2B');
      return;
    }
    const pkEmail = (newArtist.press_kit_email || '').trim().toLowerCase();
    if (!linkedDjId && (!pkEmail || !pkEmail.includes('@'))) {
      toast.error('Ingresá el email del artista para crear su press kit');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...newArtist, ...buildHighlightFields(), ...buildB2bFields(), press_kit_email: pkEmail || null };
      if (linkedDjId) {
        payload.dj_profile_id = linkedDjId.id;
        payload.dj_slug = linkedDjId.slug || null;
        payload.press_kit_email = null;
      }
      const res = await api.post(`/producers/me/events/${eventId}/lineup`, payload);
      const pkStatus = res.data?.press_kit_status;
      if (pkStatus === 'linked_existing') {
        toast.success('Artista agregado — ya tenía press kit en Bravo, quedó vinculado');
      } else if (pkStatus === 'pending_created' || pkStatus === 'pending_existing') {
        toast.success('Artista agregado — se creó su solicitud DJ, pendiente de aprobación');
      } else {
        toast.success(linkedDjId ? 'DJ vinculado al lineup' : 'Artista agregado al lineup');
      }
      setShowModal(false);
      resetForm();
      loadLineup();
    } catch (error) { toast.error('Error al agregar artista'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editingArtist) return;
    if (newArtist.b2b && !(newArtist.b2b_artist?.name || '').trim()) {
      toast.error('Ingresá el nombre del segundo artista del B2B');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...newArtist, ...buildHighlightFields(), ...buildB2bFields() };
      delete payload.press_kit_email;
      if (linkedDjId) {
        payload.dj_profile_id = linkedDjId.id;
        payload.dj_slug = linkedDjId.slug || null;
      }
      await api.put(`/producers/me/events/${eventId}/lineup/${editingArtist.id}`, payload);
      toast.success('Artista actualizado');
      setEditingArtist(null);
      setShowModal(false);
      resetForm();
      loadLineup();
    } catch (error) { toast.error('Error al actualizar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (artistId) => {
    if (!confirm('¿Eliminar este artista del lineup?')) return;
    try {
      await api.delete(`/producers/me/events/${eventId}/lineup/${artistId}`);
      toast.success('Artista eliminado');
      loadLineup();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const resetForm = () => {
    setNewArtist({
      name: '', role: 'DJ', image_url: '', start_time: '', end_time: '',
      stage: '', is_headliner: false, headliner_label: '', press_kit_email: '',
      b2b: false, b2b_artist: { name: '', image_url: '', dj_profile_id: null, dj_slug: null },
      bio: '', social_links: { instagram: '', spotify: '' }
    });
    setHlChoice('none');
    setLinkedDjId(null);
  };

  const emptyB2b = { name: '', image_url: '', dj_profile_id: null, dj_slug: null };

  const toggleB2b = async (checked) => {
    setNewArtist(prev => ({ ...prev, b2b: checked, b2b_artist: prev.b2b_artist?.name ? prev.b2b_artist : { ...emptyB2b } }));
    if (checked && djList.length === 0 && !djLoading) {
      setDjLoading(true);
      try {
        const res = await api.get('/public/djs?limit=200');
        setDjList(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* manual entry still works */ }
      finally { setDjLoading(false); }
    }
  };

  const openDJPicker = async () => {
    setShowDJPicker(true);
    if (djList.length === 0) {
      setDjLoading(true);
      try {
        const res = await api.get('/public/djs?limit=200');
        setDjList(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        toast.error('No se pudo cargar la lista de DJs');
      } finally {
        setDjLoading(false);
      }
    }
  };

  const pickDJ = (dj) => {
    // Prefill the form with the DJ's public data so the producer can set the
    // set time (horario) before saving. The tile links to /dj/{slug}.
    setLinkedDjId({ id: dj.id, slug: dj.slug || null });
    setEditingArtist(null);
    setNewArtist({
      name: dj.name || 'DJ sin nombre',
      role: 'DJ',
      image_url: dj.photo || '',
      start_time: '',
      end_time: '',
      stage: '',
      is_headliner: false,
      headliner_label: '',
      press_kit_email: '',
      b2b: false,
      b2b_artist: { name: '', image_url: '', dj_profile_id: null, dj_slug: null },
      bio: dj.bio || '',
      social_links: {
        instagram: dj.instagram_url || dj.instagram || '',
        spotify: dj.spotify_url || dj.spotify || '',
      },
    });
    setHlChoice('none');
    setShowDJPicker(false);
    setShowModal(true);
    toast.success(`${dj.name} seleccionado — definí su horario y confirmá`);
  };

  const openEditModal = (artist) => {
    setEditingArtist(artist);
    setLinkedDjId(artist.dj_profile_id ? { id: artist.dj_profile_id, slug: artist.dj_slug } : null);
    setNewArtist({
      name: artist.name,
      role: artist.role || 'DJ',
      image_url: artist.image_url || '',
      start_time: artist.start_time || '',
      end_time: artist.end_time || '',
      stage: artist.stage || '',
      is_headliner: !!artist.is_headliner,
      headliner_label: artist.headliner_label || '',
      press_kit_email: '',
      b2b: !!artist.b2b,
      b2b_artist: artist.b2b_artist || { name: '', image_url: '', dj_profile_id: null, dj_slug: null },
      bio: artist.bio || '',
      social_links: artist.social_links || { instagram: '', spotify: '' }
    });
    const lbl = (artist.headliner_label || '').trim();
    setHlChoice(!artist.is_headliner ? 'none'
      : lbl === 'Invitado Especial' ? 'guest'
      : (lbl && lbl !== 'Headliner') ? 'custom' : 'headliner');
    setShowModal(true);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="font-bold text-white">Lineup del Evento</h3>
        {isEditable && (
          <Button
            onClick={openDJPicker}
            className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
            data-testid="lineup-link-dj-btn"
          >
            <Music size={16} className="mr-2" /> Vincular DJ
          </Button>
        )}
      </div>
      
      {artists.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Music size={48} className="mx-auto mb-4 text-zinc-700" />
          <p className="font-medium">No hay artistas en el lineup</p>
          <p className="text-sm">Vinculá DJs ya creados en Bravo o cargá uno desde cero</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {artists.map((artist, i) => (
            <div key={artist.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden group">
              {/* Artist Image */}
              <div className="aspect-square bg-zinc-800 relative">
                {artist.image_url ? (
                  <>
                    <img
                      src={artist.image_url}
                      alt={artist.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Graceful fallback — many producer-uploaded URLs are
                        // hotlink-blocked Instagram/Drive/legacy Cloudinary refs.
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextElementSibling;
                        if (fb) fb.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden absolute inset-0 flex items-center justify-center bg-gradient-to-br from-violet-900/30 to-pink-900/20">
                      <span className="text-3xl font-black text-white/40 uppercase">
                        {(artist.name || '?').charAt(0)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-900/30 to-pink-900/20">
                    <span className="text-3xl font-black text-white/40 uppercase">
                      {(artist.name || '?').charAt(0)}
                    </span>
                  </div>
                )}
                {/* Order Badge */}
                <span className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs font-mono text-violet-500">
                  #{i + 1}
                </span>
                {artist.is_headliner && (
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-[#D4AF37] text-black px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider" data-testid={`headliner-badge-${artist.id}`}>
                    <Star size={10} /> {artist.headliner_label || 'Headliner'}
                  </span>
                )}
                {artist.pending_dj_profile_id && (
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" title="La solicitud DJ está pendiente de aprobación en el Sistema Interno" data-testid={`presskit-pending-badge-${artist.id}`}>
                    <Clock size={9} /> Press kit pendiente
                  </span>
                )}
                {/* Actions */}
                {isEditable && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button
                      onClick={() => openEditModal(artist)}
                      className="p-2 bg-black/70 rounded hover:bg-violet-500 hover:text-black transition-colors"
                      title={artist.dj_profile_id ? 'Editar horario / headliner' : 'Editar'}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(artist.id)}
                      className="p-2 bg-black/70 rounded hover:bg-red-500 transition-colors"
                      title={artist.dj_profile_id ? 'Quitar DJ del lineup' : 'Eliminar'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Artist Info */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-bold text-white truncate">{artist.name}</h4>
                    <p className="text-sm text-violet-500">{artist.role}</p>
                  </div>
                  {artist.dj_profile_id && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] bg-[#D4AF37]/15 text-[#D4AF37] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex-shrink-0"
                      title="Vinculado a un perfil de DJ de Bravo"
                    >
                      ★ Bravo DJ
                    </span>
                  )}
                </div>
                {artist.b2b && artist.b2b_artist?.name && (
                  <p className="text-xs text-violet-300 mt-1 font-bold" data-testid={`b2b-label-${artist.id}`}>
                    B2B con {artist.b2b_artist.name}
                  </p>
                )}
                {(artist.start_time || artist.stage) && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-zinc-400">
                    {artist.start_time && (
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {artist.start_time}{artist.end_time && ` - ${artist.end_time}`}
                      </span>
                    )}
                    {artist.stage && (
                      <span className="px-2 py-0.5 bg-zinc-800 rounded">{artist.stage}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Music className="text-violet-500" size={24} />
              {editingArtist ? 'Editar Artista' : 'Añadir Artista'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Nombre *</Label>
                <Input
                  value={newArtist.name}
                  onChange={(e) => setNewArtist(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre del artista"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Rol</Label>
                <select
                  value={newArtist.role}
                  onChange={(e) => setNewArtist(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                >
                  <option value="DJ">DJ</option>
                  <option value="Live">Live</option>
                  <option value="Band">Banda</option>
                  <option value="Host">Host</option>
                  <option value="Headliner">Headliner</option>
                  <option value="Special Guest">Invitado Especial</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400">Escenario</Label>
                <Input
                  value={newArtist.stage}
                  onChange={(e) => setNewArtist(prev => ({ ...prev, stage: e.target.value }))}
                  placeholder="Ej: Main Stage"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Hora inicio</Label>
                <Input
                  type="time"
                  value={newArtist.start_time}
                  onChange={(e) => setNewArtist(prev => ({ ...prev, start_time: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Hora fin</Label>
                <Input
                  type="time"
                  value={newArtist.end_time}
                  onChange={(e) => setNewArtist(prev => ({ ...prev, end_time: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400 flex items-center gap-2">
                  Foto del Artista
                  <span className="text-xs text-zinc-600 font-normal">(Recomendado)</span>
                </Label>
                
                {/* Image recommendations */}
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 mt-2 mb-3">
                  <div className="flex items-start gap-2">
                    <Info size={14} className="text-violet-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-zinc-400 space-y-1">
                      <p className="font-medium text-zinc-300">Recomendaciones de imagen:</p>
                      <ul className="list-disc list-inside space-y-0.5 text-zinc-500">
                        <li>Formato cuadrado (1:1) - 500x500 px mínimo</li>
                        <li>Fondo oscuro o neutro preferido</li>
                        <li>JPG o PNG de alta calidad</li>
                        <li>Máximo 5MB por imagen</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Show current image if exists */}
                {newArtist.image_url && (
                  <div className="mb-3 relative">
                    <img 
                      src={newArtist.image_url} 
                      alt="Preview" 
                      className="w-24 h-24 object-cover rounded-lg border border-zinc-700"
                    />
                    <button
                      type="button"
                      onClick={() => setNewArtist(prev => ({ ...prev, image_url: '' }))}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 text-xs"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Cloudinary uploader */}
                <CloudinaryUploader
                  onUploadComplete={(result) => {
                    setNewArtist(prev => ({ ...prev, image_url: result.url }));
                  }}
                  folder="bravo/artists"
                  resourceType="image"
                  accept="image/*"
                  maxSize={5 * 1024 * 1024}
                  buttonText="Subir foto del artista"
                  showPreview={false}
                  showRequirements={false}
                  className="rounded-lg"
                />
              </div>
              <div className="col-span-2 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg space-y-2">
                <Label className="text-white text-sm font-bold flex items-center gap-1.5">
                  <Star size={13} className="text-[#D4AF37]" /> Destacar arriba del line-up
                </Label>
                <select
                  value={hlChoice}
                  onChange={(e) => setHlChoice(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                  data-testid="lineup-highlight-select"
                >
                  <option value="none">No destacar (orden por horario)</option>
                  <option value="headliner">Headliner</option>
                  <option value="guest">Invitado Especial</option>
                  <option value="custom">Otra etiqueta…</option>
                </select>
                {hlChoice === 'custom' && (
                  <Input
                    value={newArtist.headliner_label}
                    onChange={(e) => setNewArtist(prev => ({ ...prev, headliner_label: e.target.value }))}
                    placeholder='Ej: "Special B2B", "Showcase", "Live Set"'
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="lineup-highlight-custom-input"
                  />
                )}
                {hlChoice !== 'none' && (
                  <p className="text-xs text-zinc-500">
                    Aparece primero en la página del evento con la etiqueta en dorado, aunque su set no sea el de cierre.
                  </p>
                )}
              </div>
              <div className="col-span-2 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg space-y-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!newArtist.b2b}
                    onChange={(e) => toggleB2b(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="lineup-b2b-checkbox"
                  />
                  <span className="text-sm font-bold text-white">Set B2B — dos artistas en el mismo horario</span>
                </label>
                {newArtist.b2b && (
                  <div className="space-y-3 pl-1">
                    <div>
                      <Label className="text-xs text-zinc-500">Elegir DJ de Bravo (opcional)</Label>
                      <select
                        value={newArtist.b2b_artist?.dj_profile_id || ''}
                        onChange={(e) => {
                          const dj = djList.find(d => d.id === e.target.value);
                          setNewArtist(prev => ({
                            ...prev,
                            b2b_artist: dj
                              ? { name: dj.name, image_url: dj.photo || '', dj_profile_id: dj.id, dj_slug: dj.slug || null }
                              : { ...(prev.b2b_artist || {}), dj_profile_id: null, dj_slug: null }
                          }));
                        }}
                        className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                        data-testid="lineup-b2b-dj-select"
                      >
                        <option value="">{djLoading ? 'Cargando DJs…' : 'Cargar manualmente'}</option>
                        {djList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Nombre del segundo artista *</Label>
                      <Input
                        value={newArtist.b2b_artist?.name || ''}
                        onChange={(e) => setNewArtist(prev => ({ ...prev, b2b_artist: { ...(prev.b2b_artist || {}), name: e.target.value } }))}
                        placeholder="Nombre del artista B2B"
                        className="bg-zinc-800 border-zinc-700 mt-1"
                        data-testid="lineup-b2b-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Foto del segundo artista</Label>
                      {newArtist.b2b_artist?.image_url && (
                        <div className="my-2 relative inline-block">
                          <img src={newArtist.b2b_artist.image_url} alt="" className="w-16 h-16 object-cover rounded-full border border-zinc-700" />
                          <button
                            type="button"
                            onClick={() => setNewArtist(prev => ({ ...prev, b2b_artist: { ...(prev.b2b_artist || {}), image_url: '' } }))}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 text-xs"
                          >×</button>
                        </div>
                      )}
                      <CloudinaryUploader
                        onUploadComplete={(result) => setNewArtist(prev => ({ ...prev, b2b_artist: { ...(prev.b2b_artist || {}), image_url: result.url } }))}
                        folder="bravo/artists"
                        resourceType="image"
                        accept="image/*"
                        maxSize={5 * 1024 * 1024}
                        buttonText="Subir foto del artista B2B"
                        showPreview={false}
                        showRequirements={false}
                        className="rounded-lg mt-1"
                      />
                    </div>
                    <p className="text-xs text-zinc-500">
                      En la página del evento se muestran los dos artistas juntos con sus fotos intersectadas y "B2B" en el medio.
                    </p>
                  </div>
                )}
              </div>
              {!linkedDjId && !editingArtist && (
                <div className="col-span-2">
                  <Label className="text-zinc-400">Email del artista *</Label>
                  <Input
                    type="email"
                    value={newArtist.press_kit_email}
                    onChange={(e) => setNewArtist(prev => ({ ...prev, press_kit_email: e.target.value }))}
                    placeholder="artista@gmail.com"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    data-testid="lineup-presskit-email-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1.5">
                    Se crea una solicitud DJ pendiente de aprobación. Al aprobarse, ese email podrá gestionar su press kit desde el Portal DJ y el artista queda vinculado automáticamente.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">Cancelar</Button>
            <Button 
              onClick={editingArtist ? handleUpdate : handleCreate}
              disabled={saving}
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
            >
              {saving && <Loader2 className="animate-spin mr-2" size={16} />}
              {editingArtist ? 'Guardar Cambios' : 'Añadir Artista'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DJ Picker Dialog — link an existing Bravo DJ profile */}
      <Dialog open={showDJPicker} onOpenChange={setShowDJPicker}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Music size={18} className="text-[#D4AF37]" />
              Vincular DJ existente
            </DialogTitle>
            <p className="text-xs text-zinc-400 mt-1">
              Seleccioná un DJ ya creado en Bravo. Se agrega al lineup al instante y, al tocarlo en la página del evento, los asistentes van directo a su press kit.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <input
              type="text"
              value={djQuery}
              onChange={(e) => setDjQuery(e.target.value)}
              placeholder="Buscar por nombre, ciudad o género…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/40"
              data-testid="dj-picker-search"
            />
            <div className="max-h-[420px] overflow-y-auto rounded-xl border border-zinc-800 divide-y divide-zinc-900">
              {djLoading ? (
                <div className="py-10 text-center text-zinc-500">
                  <Loader2 className="mx-auto animate-spin" size={20} />
                  <p className="text-xs mt-2">Cargando DJs…</p>
                </div>
              ) : (() => {
                const q = djQuery.trim().toLowerCase();
                const filtered = djList.filter((d) => {
                  if (!q) return true;
                  const hay = [d.name, d.city, ...(d.genres || [])].filter(Boolean).join(' ').toLowerCase();
                  return hay.includes(q);
                });
                if (filtered.length === 0) {
                  return (
                    <div className="py-10 text-center text-zinc-500">
                      <p className="text-sm">Sin resultados.</p>
                      <p className="text-xs mt-1">Probá con otra búsqueda o agregalo desde cero.</p>
                    </div>
                  );
                }
                return filtered.map((dj) => (
                  <button
                    key={dj.id}
                    type="button"
                    onClick={() => pickDJ(dj)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900 transition-colors text-left"
                    data-testid={`dj-picker-item-${dj.id}`}
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-zinc-800 border border-zinc-700 flex-shrink-0">
                      {dj.photo ? (
                        <img src={dj.photo} alt={dj.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-500">
                          <Music size={16} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{dj.name}</p>
                      <p className="text-[11px] text-zinc-500 truncate">
                        {(dj.genres || []).slice(0, 3).join(' · ')}
                        {dj.city && <span> · {dj.city}</span>}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold flex-shrink-0">
                      Vincular →
                    </span>
                  </button>
                ));
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDJPicker(false)} className="border-zinc-700">
              Cancelar
            </Button>
            <Button
              onClick={() => { setShowDJPicker(false); resetForm(); setEditingArtist(null); setShowModal(true); }}
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
            >
              <Plus size={14} className="mr-1.5" /> Cargar desde cero
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const InvitationsTab = ({ eventId, isEditable }) => {
  const [invitations, setInvitations] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newInvitation, setNewInvitation] = useState({ email: '', tier_id: '', guests: [{ name: '', dni: '' }] });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { loadInvitations(); loadTiers(); }, []);

  const loadInvitations = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/invitations`);
      setInvitations(response.data || []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const loadTiers = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/tickets`);
      setTiers(Array.isArray(response.data) ? response.data : []);
    } catch { /* tiers optional */ }
  };

  const setGuestCount = (count) => {
    setNewInvitation(prev => {
      const guests = [...prev.guests];
      while (guests.length < count) guests.push({ name: '', dni: '' });
      return { ...prev, guests: guests.slice(0, count) };
    });
  };

  const updateGuest = (idx, field, value) => {
    setNewInvitation(prev => {
      const guests = prev.guests.map((g, i) => (i === idx ? { ...g, [field]: value } : g));
      return { ...prev, guests };
    });
  };

  const handleCreate = async () => {
    if (!newInvitation.email.trim() || !newInvitation.email.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }
    const cleanGuests = [];
    const seenDnis = new Set();
    for (let i = 0; i < newInvitation.guests.length; i++) {
      const g = newInvitation.guests[i];
      const name = g.name.trim();
      const dni = g.dni.replace(/\./g, '').replace(/-/g, '').replace(/\s/g, '').trim();
      if (!name) { toast.error(`Ingresa el nombre del invitado #${i + 1}`); return; }
      if (!dni || dni.length < 6) { toast.error(`DNI inválido para el invitado #${i + 1} (mínimo 6 dígitos)`); return; }
      if (seenDnis.has(dni)) { toast.error(`El DNI ${dni} está repetido en la lista`); return; }
      seenDnis.add(dni);
      cleanGuests.push({ name, dni });
    }
    setSaving(true);
    try {
      const payload = {
        email: newInvitation.email,
        guests: cleanGuests,
        tier_id: newInvitation.tier_id || null,
      };
      await api.post(`/producers/me/events/${eventId}/invitations`, payload);
      toast.success(cleanGuests.length > 1
        ? `¡${cleanGuests.length} invitaciones creadas! Se envió un email con un QR por invitado`
        : '¡Invitación creada y enviada con QR por email!');
      setShowModal(false);
      setNewInvitation({ email: '', tier_id: '', guests: [{ name: '', dni: '' }] });
      loadInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear invitación');
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`¿Eliminar la invitación de ${inv.name}? El QR enviado dejará de ser válido.`)) return;
    setDeletingId(inv.id);
    try {
      await api.delete(`/producers/me/events/${eventId}/invitations/${inv.id}`);
      toast.success('Invitación eliminada');
      setInvitations(prev => prev.filter(i => i.id !== inv.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    finally { setDeletingId(null); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" size={32} /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-white">Invitaciones y Cortesías</h3>
          <p className="text-zinc-500 text-sm mt-1">Crea invitaciones con código QR único. Se envía automáticamente al email del invitado.</p>
        </div>
        {isEditable && (
          <Button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white">
            <Plus size={16} className="mr-2" />Nueva Invitación
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{invitations.length}</p>
          <p className="text-zinc-500 text-sm">Total invitaciones</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-blue-400">{invitations.reduce((acc, i) => acc + (i.quantity || 1), 0)}</p>
          <p className="text-zinc-500 text-sm">Total entradas</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-400">{invitations.reduce((acc, i) => acc + (i.redeemed_count || (i.redeemed ? (i.quantity || 1) : 0)), 0)}</p>
          <p className="text-zinc-500 text-sm">Canjeadas</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-yellow-400">{invitations.reduce((acc, i) => acc + ((i.quantity || 1) - (i.redeemed_count || (i.redeemed ? (i.quantity || 1) : 0))), 0)}</p>
          <p className="text-zinc-500 text-sm">Pendientes</p>
        </div>
      </div>

      {/* List */}
      {invitations.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900/30 rounded-xl border border-dashed border-zinc-800">
          <UserPlus size={48} className="mx-auto mb-4 text-zinc-700" />
          <p className="text-zinc-500">No hay invitaciones creadas</p>
          <p className="text-zinc-600 text-sm mt-1">Crea una invitación para tus invitados VIP</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invitations.map((inv, i) => (
            <div key={inv.id || i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  {inv.qr_url ? (
                    <img src={inv.qr_url} alt="QR" className="w-14 h-14 rounded-lg bg-white p-1" />
                  ) : (
                    <div className="w-14 h-14 bg-violet-500/20 rounded-lg flex items-center justify-center">
                      <UserPlus size={24} className="text-violet-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-bold text-white">{inv.name}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-sm text-zinc-400">{inv.email}</span>
                      {inv.dni && (
                        <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">DNI: {inv.dni}</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      {inv.tier_name ? `Tanda: ${inv.tier_name}` : 'Cortesía'}
                      {(inv.quantity || 1) > 1 && ` • ${inv.quantity} entradas`}
                      {inv.redeemed_count > 0 && ` • ${inv.redeemed_count} usada${inv.redeemed_count > 1 ? 's' : ''}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {inv.code && (
                    <button 
                      onClick={() => copyCode(inv.code)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                    >
                      <span className="font-mono text-sm text-violet-500">{inv.code}</span>
                      <Copy size={14} className="text-zinc-400" />
                    </button>
                  )}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    inv.redeemed 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {inv.redeemed ? 'Canjeada' : 'Pendiente'}
                  </span>
                  {isEditable && !inv.redeemed && !(inv.redeemed_count > 0) && (
                    <button
                      onClick={() => handleDelete(inv)}
                      disabled={deletingId === inv.id}
                      className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Eliminar invitación"
                      data-testid={`delete-invitation-${inv.id}`}
                    >
                      {deletingId === inv.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6"
            >
              <h3 className="text-xl font-bold text-white mb-2">Nueva Invitación</h3>
              <p className="text-zinc-500 text-sm mb-6">Un QR único por invitado. Se envían todos juntos al email indicado.</p>

              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400">Email *</Label>
                  <Input
                    type="email"
                    value={newInvitation.email}
                    onChange={(e) => setNewInvitation(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="juan@email.com"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    data-testid="invitation-email"
                  />
                  <p className="text-xs text-zinc-600 mt-1">Se enviarán los QR a este email</p>
                </div>
                {tiers.length > 0 && (
                  <div>
                    <Label className="text-zinc-400">Tanda (opcional)</Label>
                    <select
                      value={newInvitation.tier_id}
                      onChange={(e) => setNewInvitation(prev => ({ ...prev, tier_id: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2 mt-1"
                      data-testid="invitation-tier-select"
                    >
                      <option value="">Cortesía (sin tanda)</option>
                      {tiers.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-zinc-600 mt-1">El ticket se envía identificado con la tanda elegida</p>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-400">Invitados ({newInvitation.guests.length})</Label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setGuestCount(Math.max(1, newInvitation.guests.length - 1))}
                        className="w-8 h-8 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center justify-center text-white"
                        data-testid="invitation-qty-minus"
                      >-</button>
                      <button
                        onClick={() => setGuestCount(Math.min(10, newInvitation.guests.length + 1))}
                        className="w-8 h-8 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center justify-center text-white"
                        data-testid="invitation-qty-plus"
                      >+</button>
                    </div>
                  </div>
                  <div className="space-y-2 mt-2 max-h-64 overflow-y-auto pr-1">
                    {newInvitation.guests.map((guest, idx) => (
                      <div key={idx} className="grid grid-cols-2 gap-2 bg-zinc-800/40 border border-zinc-800 rounded-lg p-2" data-testid={`invitation-guest-row-${idx}`}>
                        <Input
                          value={guest.name}
                          onChange={(e) => updateGuest(idx, 'name', e.target.value)}
                          placeholder={`Nombre y apellido #${idx + 1}`}
                          className="bg-zinc-800 border-zinc-700"
                          data-testid={`invitation-guest-name-${idx}`}
                        />
                        <Input
                          value={guest.dni}
                          onChange={(e) => updateGuest(idx, 'dni', e.target.value)}
                          placeholder="DNI"
                          className="bg-zinc-800 border-zinc-700"
                          data-testid={`invitation-guest-dni-${idx}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">El DNI de cada invitado se verifica en la puerta y no puede repetirse</p>
                </div>
              </div>

              <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 mt-4">
                <p className="text-xs text-violet-300">
                  <QrCode size={12} className="inline mr-1" />
                  Cada invitado recibe su propio QR. En la puerta pueden ingresar con el QR o con su DNI
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-zinc-700">
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 text-white" data-testid="create-invitation-btn">
                  {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                  Crear y Enviar
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// RRPP Tab
const RRPPTab = ({ eventId, isEditable }) => {
  const [rrpps, setRrpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newRrpp, setNewRrpp] = useState({ name: '', commission_percent: 10 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadRrpps(); }, []);

  const loadRrpps = async () => {
    try {
      const response = await api.get(`/producers/me/events/${eventId}/rrpp`);
      setRrpps(response.data || []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newRrpp.name.trim()) { toast.error('Ingresa el nombre del RRPP'); return; }
    setSaving(true);
    try {
      const response = await api.post(`/producers/me/events/${eventId}/rrpp`, newRrpp);
      toast.success('¡RRPP agregado! Link de venta generado');
      setShowModal(false);
      setNewRrpp({ name: '', commission_percent: 10 });
      loadRrpps();
    } catch (error) { toast.error('Error al agregar RRPP'); }
    finally { setSaving(false); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado al portapapeles');
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  // Calculate totals
  const totalSales = rrpps.reduce((acc, r) => acc + (r.sales_count || 0), 0);
  const totalAmount = rrpps.reduce((acc, r) => acc + (r.total_sales_amount || 0), 0);
  const totalCommissions = rrpps.reduce((acc, r) => acc + (r.commission_earned || 0), 0);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-violet-500" size={32} /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-white">Equipo RRPP</h3>
          <p className="text-zinc-500 text-sm mt-1">Cada RRPP recibe un link único para trackear sus ventas</p>
        </div>
        {isEditable && (
          <Button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white">
            <Plus size={16} className="mr-2" />Agregar RRPP
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{rrpps.length}</p>
          <p className="text-zinc-500 text-sm">RRPPs activos</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-violet-500">{totalSales}</p>
          <p className="text-zinc-500 text-sm">Ventas totales</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-400">${totalAmount.toLocaleString()}</p>
          <p className="text-zinc-500 text-sm">Monto vendido</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-orange-400">${totalCommissions.toLocaleString()}</p>
          <p className="text-zinc-500 text-sm">Comisiones</p>
        </div>
      </div>

      {/* List */}
      {rrpps.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900/30 rounded-xl border border-dashed border-zinc-800">
          <Users size={48} className="mx-auto mb-4 text-zinc-700" />
          <p className="text-zinc-500">No hay RRPPs en el equipo</p>
          <p className="text-zinc-600 text-sm mt-1">Agrega RRPPs para generar links de venta personalizados</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rrpps.map((rrpp, i) => (
            <div key={rrpp.id || i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Info */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500/30 to-violet-500/10 rounded-full flex items-center justify-center">
                    <Users size={20} className="text-violet-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg">{rrpp.name}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm text-violet-500 font-medium">{rrpp.commission_percent}% comisión</span>
                      <span className="text-zinc-600">•</span>
                      <span className="text-sm text-zinc-400">{rrpp.sales_count || 0} venta{(rrpp.sales_count || 0) !== 1 ? 's' : ''}</span>
                      {rrpp.total_sales_amount > 0 && (
                        <>
                          <span className="text-zinc-600">•</span>
                          <span className="text-sm text-green-400">${rrpp.total_sales_amount?.toLocaleString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Code */}
                  <button 
                    onClick={() => copyCode(rrpp.code)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                  >
                    <span className="font-mono text-sm text-zinc-300">{rrpp.code}</span>
                    <Copy size={14} className="text-zinc-500" />
                  </button>
                  
                  {/* Sale Link */}
                  {rrpp.sale_link && (
                    <button 
                      onClick={() => copyLink(rrpp.sale_link)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-500 rounded-lg transition-colors"
                    >
                      <Link size={14} />
                      <span className="text-sm font-medium">Copiar Link de Venta</span>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Sales by Tier - Detailed breakdown */}
              {rrpp.sales_by_tier && Object.keys(rrpp.sales_by_tier).length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-3 flex items-center gap-1">
                    <BarChart3 size={12} />
                    Detalle de ventas por tanda
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Object.entries(rrpp.sales_by_tier).map(([tierName, tierData]) => (
                      <div 
                        key={tierName} 
                        className="bg-zinc-800/50 rounded-lg px-3 py-2 border border-zinc-700/50"
                      >
                        <p className="text-xs text-zinc-400 truncate">{tierName}</p>
                        <div className="flex items-baseline justify-between gap-2 mt-1">
                          <span className="text-sm font-bold text-white">{tierData.count}</span>
                          <span className="text-xs text-green-400">${tierData.amount?.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Commission earned */}
                  {rrpp.commission_earned > 0 && (
                    <div className="mt-3 flex items-center justify-between bg-violet-500/10 rounded-lg px-3 py-2">
                      <span className="text-xs text-violet-500">Comisión ganada:</span>
                      <span className="text-sm font-bold text-violet-500">${rrpp.commission_earned?.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Sale Link Preview */}
              {rrpp.sale_link && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Link de venta personalizado:</p>
                  <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2">
                    <span className="text-xs text-zinc-400 truncate flex-1 font-mono">{rrpp.sale_link}</span>
                    <button 
                      onClick={() => window.open(rrpp.sale_link, '_blank')}
                      className="text-zinc-400 hover:text-white transition-colors"
                    >
                      <ExternalLink size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6"
            >
              <h3 className="text-xl font-bold text-white mb-2">Agregar RRPP</h3>
              <p className="text-zinc-500 text-sm mb-6">Se generará un link único de venta para este RRPP</p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400">Nombre *</Label>
                  <Input 
                    value={newRrpp.name} 
                    onChange={(e) => setNewRrpp(prev => ({ ...prev, name: e.target.value }))} 
                    placeholder="Ej: María González"
                    className="bg-zinc-800 border-zinc-700 mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Comisión por venta (%)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input
                      type="range"
                      min="0"
                      max="30"
                      value={newRrpp.commission_percent}
                      onChange={(e) => setNewRrpp(prev => ({ ...prev, commission_percent: parseInt(e.target.value) }))}
                      className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-violet-500"
                    />
                    <span className="text-lg font-bold text-violet-500 w-12 text-center">{newRrpp.commission_percent}%</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-3 mt-4">
                <p className="text-xs text-zinc-400">
                  <Link size={12} className="inline mr-1" />
                  Al crear, se generará automáticamente un link de venta único que podrás compartir con el RRPP
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-zinc-700">
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-gradient-to-r from-violet-600 to-pink-600 text-white">
                  {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                  Agregar RRPP
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Scanner Tab
const ScannerTab = ({ eventId }) => {
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [recentScans, setRecentScans] = useState([]);

  const handleScan = async () => {
    if (!code.trim()) {
      toast.error('Ingresa un código');
      return;
    }

    setScanning(true);
    setResult(null);

    try {
      // Determine code type (INV- for invitations, others for tickets)
      const codeUpper = code.trim().toUpperCase();
      
      if (codeUpper.startsWith('INV-')) {
        // Validate invitation
        const response = await api.post(`/producers/me/events/${eventId}/invitations/validate`, {
          code: codeUpper
        });
        
        if (response.data.success) {
          setResult({
            type: 'invitation',
            success: true,
            message: response.data.message,
            data: response.data.invitation
          });
          toast.success(response.data.message);
        } else {
          setResult({
            type: 'invitation',
            success: false,
            message: response.data.message,
            data: response.data.invitation
          });
          toast.error(response.data.message);
        }
      } else {
        // Validate ticket (existing flow)
        const response = await api.post(`/producers/me/events/${eventId}/tickets/validate`, {
          code: codeUpper
        });
        
        if (response.data.success) {
          setResult({
            type: 'ticket',
            success: true,
            message: response.data.message || '¡Entrada válida!',
            data: response.data.ticket
          });
          toast.success(response.data.message || '¡Entrada válida!');
        } else {
          setResult({
            type: 'ticket',
            success: false,
            message: response.data.message || 'Entrada no válida',
            data: response.data.ticket
          });
          toast.error(response.data.message || 'Entrada no válida');
        }
      }

      // Add to recent scans
      setRecentScans(prev => [{
        code: codeUpper,
        time: new Date().toLocaleTimeString(),
        success: true
      }, ...prev.slice(0, 9)]);

    } catch (error) {
      const message = error.response?.data?.detail || 'Código no encontrado';
      setResult({
        type: 'unknown',
        success: false,
        message
      });
      toast.error(message);
      
      setRecentScans(prev => [{
        code: code.trim().toUpperCase(),
        time: new Date().toLocaleTimeString(),
        success: false
      }, ...prev.slice(0, 9)]);
    } finally {
      setScanning(false);
      setCode('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-bold text-white">Escáner de Entradas e Invitaciones</h3>
        <p className="text-zinc-500 text-sm mt-1">
          Ingresa códigos de tickets o invitaciones (INV-XXXXX) para validarlos
        </p>
      </div>

      {/* Scanner Input */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && handleScan()}
              placeholder="Ingresa código (ej: INV-ABC123 o TICKET-XYZ)"
              className="bg-zinc-800 border-zinc-700 font-mono text-lg h-12"
            />
          </div>
          <Button 
            onClick={handleScan}
            disabled={scanning}
            className="bg-gradient-to-r from-violet-600 to-pink-600 text-white h-12 px-8"
          >
            {scanning ? <Loader2 className="animate-spin mr-2" size={18} /> : <QrCode size={18} className="mr-2" />}
            Validar
          </Button>
        </div>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 p-4 rounded-xl ${
              result.success 
                ? 'bg-green-500/10 border border-green-500/30' 
                : 'bg-red-500/10 border border-red-500/30'
            }`}
          >
            <div className="flex items-center gap-3">
              {result.success ? (
                <CheckCircle className="text-green-400" size={24} />
              ) : (
                <XCircle className="text-red-400" size={24} />
              )}
              <div>
                <p className={`font-bold ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                  {result.message}
                </p>
                {result.data && (
                  <p className="text-zinc-400 text-sm mt-1">
                    {result.type === 'invitation' && result.data.name && (
                      <>Invitado: {result.data.name} • {result.data.quantity} entrada(s)</>
                    )}
                    {result.type === 'ticket' && result.data.tier_name && (
                      <>Tipo: {result.data.tier_name}</>
                    )}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Code Types Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-violet-500/20 rounded-lg flex items-center justify-center">
              <Ticket size={16} className="text-violet-500" />
            </div>
            <h4 className="font-bold text-white">Tickets</h4>
          </div>
          <p className="text-zinc-500 text-sm">Códigos de entradas compradas</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <UserPlus size={16} className="text-purple-400" />
            </div>
            <h4 className="font-bold text-white">Invitaciones</h4>
          </div>
          <p className="text-zinc-500 text-sm">Códigos INV-XXXXX de cortesías</p>
        </div>
      </div>

      {/* Recent Scans */}
      {recentScans.length > 0 && (
        <div>
          <h4 className="font-bold text-white mb-3">Escaneos recientes</h4>
          <div className="space-y-2">
            {recentScans.map((scan, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-3 rounded-lg ${
                  scan.success ? 'bg-green-500/5 border border-green-500/20' : 'bg-red-500/5 border border-red-500/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  {scan.success ? (
                    <CheckCircle size={16} className="text-green-400" />
                  ) : (
                    <XCircle size={16} className="text-red-400" />
                  )}
                  <span className="font-mono text-sm text-zinc-300">{scan.code}</span>
                </div>
                <span className="text-xs text-zinc-500">{scan.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to full scanner */}
      <div className="text-center pt-4 border-t border-zinc-800">
        <p className="text-zinc-500 text-sm mb-3">¿Necesitas escanear con cámara?</p>
        <Button 
          variant="outline"
          onClick={() => window.location.href = '/producer/scanner'} 
          className="border-zinc-700"
        >
          <QrCode size={16} className="mr-2" />
          Ir al Escáner con Cámara
        </Button>
      </div>
    </div>
  );
};

export default EventManagePage;
